import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { PageHeader } from '../../components/common/PageHeader';
import { Modal } from '../../components/common/Modal';
import { LessonItem, CourseSection, UserProfile, SyllabusGradingRecord, UjianBengkungRegistration, NoticeItem, GelanggangDetail } from '../../types';
import {
  getSyllabusDB,
  saveSyllabusDB,
  getMembersDB,
  updateLessonDB,
  getMemberSyllabusProgressDB,
  saveMemberSyllabusProgressDB,
  getFamilySyllabusProgressSummaryDB,
  getSyllabusGradingDB,
  saveSyllabusGradingDB,
  getMemberUjianBengkungRegistrationDB,
  saveUjianBengkungRegistrationDB,
  getActiveUjianBengkungEventDB,
  getGelanggangDB
} from '../../services/db';
import { getNextBengkungRank, getSilatRankFromBengkung, cleanBengkungTitle, toDbBengkungCode, getBengkungRankIndex, isPreviousBengkungRank, getPreviousRankLessonIds, isEligibleForTunasPendekar, isTunasPendekarSection } from '../../data/bengkungRanks';
import { DashboardSyllabusManager } from '../../components/syllabus/DashboardSyllabusManager';
import { isSuperAdmin, formatPublicName, canUserAddChild, sanitizeMemberId, isChildOfParent, hasRolePermission, getHighestRole, getUserRoles, hasUserRole } from '../../utils/formatters';
import {
  BookOpen,
  Video,
  FileText,
  Download,
  Play,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileDown,
  CheckSquare,
  Square,
  Lock,
  ShieldAlert,
  Award,
  Plus,
  Trash2,
  Edit3,
  Settings,
  Users,
  User,
  Sparkles,
  CheckCircle2,
  Calendar,
  AlertTriangle,
  ShieldCheck,
  HelpCircle,
  Send,
  Check,
  Star,
  MessageSquare,
  Lightbulb,
  ArrowLeft,
  Search,
  X
} from 'lucide-react';

const RATING_LEVELS = [
  { value: 1, label: 'Tahap 1: Asas', desc: 'Perlu bimbingan rapi guru/jurulatih dari asas langkah dan gerak.', badgeColor: 'bg-rose-100 text-rose-800 border-rose-200' },
  { value: 2, label: 'Tahap 2: Sederhana Rendah', desc: 'Boleh melakukan gerak asas tetapi masih kaku, perlu perbanyak latihan.', badgeColor: 'bg-orange-100 text-orange-800 border-orange-200' },
  { value: 3, label: 'Tahap 3: Memuaskan', desc: 'Menguasai asas gerak, jurus, dan susun atur langkah silat dengan baik.', badgeColor: 'bg-amber-100 text-amber-800 border-amber-200' },
  { value: 4, label: 'Tahap 4: Baik', desc: 'Pukulan bertenaga, kekuda mantap, dan gerak lincah menepati piawaian silat.', badgeColor: 'bg-blue-100 text-blue-800 border-blue-200' },
  { value: 5, label: 'Tahap 5: Cemerlang', desc: 'Penguasaan mantap, hayatan jurus sempurna, beradab tinggi, dan berpadu.', badgeColor: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
];

export const SyllabusPage: React.FC = () => {
  const { currentUser, selectedChildId, setSelectedChildId } = useAuth();
  const { showToast } = useToast();
  const [sections, setSections] = useState<CourseSection[]>([]);
  const [openSectionIds, setOpenSectionIds] = useState<string[]>(['sec-hitam']);

  const toggleSectionOpen = (sectionId: string) => {
    setOpenSectionIds(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  // Fetch members to locate children & family members for multi-user syllabus
  const [members, setMembers] = useState<UserProfile[]>([]);
  const [gelanggangList, setGelanggangList] = useState<GelanggangDetail[]>([]);
  const [selectedGelanggangMemberId, setSelectedGelanggangMemberId] = useState<string | null>(null);

  useEffect(() => {
    getMembersDB().then(setMembers).catch(() => { });
    getGelanggangDB().then(setGelanggangList).catch(() => { });
  }, []);

  // Children / family members linked to current user
  const familyMembers = useMemo(() => {
    if (!currentUser) return [];
    return members.filter(m => isChildOfParent(m, currentUser));
  }, [members, currentUser]);

  const hasFamily = familyMembers.length > 0;
  const isCurrentUserPesilat = currentUser && (!hasUserRole(currentUser, 'Waris') || getUserRoles(currentUser).some(r => r !== 'Waris') || Boolean((currentUser as any).isAlsoPesilat));

  // All eligible pesilat learners (excludes Waris non-pesilat)
  const pesilatLearners = useMemo(() => {
    const list: UserProfile[] = [];
    if (isCurrentUserPesilat && currentUser) {
      const liveUser = members.find(m => m.id === currentUser.id) || currentUser;
      list.push(liveUser);
    }
    list.push(...familyMembers);
    return list;
  }, [isCurrentUserPesilat, currentUser, familyMembers, members]);

  const isSinglePesilat = pesilatLearners.length === 1;

  // Active member for syllabus progression (can also be a student selected by Ketua Gelanggang / Pengurusan)
  const activeMember = useMemo(() => {
    if (!currentUser) return null;
    if (selectedGelanggangMemberId) {
      const foundInMembers = members.find(m => m.id === selectedGelanggangMemberId);
      if (foundInMembers) return foundInMembers;
    }
    if (selectedChildId) {
      const found = pesilatLearners.find(m => m.id === selectedChildId);
      if (found) return found;
    }
    // If Waris role (non-pesilat), activeMember must default to the first pesilat child
    if (!isCurrentUserPesilat) {
      return familyMembers[0] || null;
    }
    return members.find(m => m.id === currentUser.id) || currentUser;
  }, [currentUser, selectedGelanggangMemberId, selectedChildId, isCurrentUserPesilat, pesilatLearners, familyMembers, members]);


  const [activeLesson, setActiveLesson] = useState<LessonItem>({
    id: 'les-07',
    title: 'Senaman Satu Tiang & Serangan Luar/Dalam',
    type: 'video',
    duration: '15:20',
    completed: false,
    description: 'Latihan pengukuhan satu tiang, serangan berangkai luar & dalam, dan kombinasi pukulan lapis.'
  });
  const [isPlayingVideo, setIsPlayingVideo] = useState<boolean>(false);

  // Map of completed lesson IDs for all family members for quick summary badges
  const [familyProgressMap, setFamilyProgressMap] = useState<Record<string, string[]>>({});

  // Modal for lesson creation
  const [isAddLessonModalOpen, setIsAddLessonModalOpen] = useState(false);
  const [newLessonTitle, setNewLessonTitle] = useState('');
  const [newLessonType, setNewLessonType] = useState<'video' | 'pdf' | 'reading'>('video');
  const [newLessonDuration, setNewLessonDuration] = useState('10:00');
  const [newLessonDescription, setNewLessonDescription] = useState('');
  const [newLessonTargetRank, setNewLessonTargetRank] = useState<'hitam' | 'putih' | 'merah' | 'kuning'>('hitam');

  // Delete confirmation
  const [confirmDeleteLessonId, setConfirmDeleteLessonId] = useState<{ id: string; title: string } | null>(null);

  // Edit lesson modal state
  const [isEditLessonModalOpen, setIsEditLessonModalOpen] = useState(false);
  const [editLessonData, setEditLessonData] = useState<{ lesson: LessonItem; sectionId: string } | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editType, setEditType] = useState<'video' | 'pdf' | 'reading'>('video');
  const [editDuration, setEditDuration] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editVideoUrl, setEditVideoUrl] = useState('');
  const [editPdfFileId, setEditPdfFileId] = useState('');

  // Management mode toggle
  const [showManagementPanel, setShowManagementPanel] = useState(false);

  // Management Roles can access all modules; Ahli roles access based on their certified Bengkung rank
  const isManagement = currentUser && (isSuperAdmin(currentUser) || hasRolePermission(currentUser, ['Pengerusi', 'Timbalan Pengerusi', 'Setiausaha', 'Pen. Setiausaha', 'Bendahari', 'Ketua Gelanggang', 'Bendahari Gelanggang', 'AJK']));

  const userBengkungRank = (activeMember?.bengkung || '').toLowerCase();

  // Load family progress summary
  useEffect(() => {
    if (!currentUser) return;
    const allRelevantIds = [currentUser.id, ...familyMembers.map(m => m.id)];
    getFamilySyllabusProgressSummaryDB(allRelevantIds, members).then(setFamilyProgressMap).catch(() => { });
  }, [currentUser, familyMembers, members, sections]);

  const isRankUnlocked = (rank: CourseSection['bengkungRank'] | string): boolean => {
    if (isManagement) return true;
    if (!activeMember?.bengkung) {
      if (rank.startsWith('hitam') || rank.startsWith('putih')) return true;
      return false;
    }
    const memberIdx = getBengkungRankIndex(activeMember.bengkung);
    const sectionIdx = getBengkungRankIndex(rank);
    if (memberIdx !== -1 && sectionIdx !== -1) {
      // Unlocks all previous levels plus the current level
      return sectionIdx <= memberIdx;
    }
    if (rank.startsWith('hitam')) return true; // Everyone can access Hitam (youngest/earliest foundation)
    if (rank.startsWith('putih')) return userBengkungRank.includes('putih') || userBengkungRank.includes('merah') || userBengkungRank.includes('kuning') || userBengkungRank.includes('biru');
    if (rank.startsWith('merah')) return userBengkungRank.includes('merah') || userBengkungRank.includes('kuning');
    if (rank.startsWith('kuning')) return userBengkungRank.includes('kuning');
    return false;
  };

  // Load syllabus from DB and completed lessons specifically for activeMember
  useEffect(() => {
    if (!activeMember) return;
    getSyllabusDB().then(async (dbSections) => {
      try {
        const savedCompleted = await getMemberSyllabusProgressDB(activeMember.id, activeMember.bengkung);
        const prevLevelLessonIds = getPreviousRankLessonIds(activeMember.bengkung, dbSections);
        const allCompletedIds = Array.from(new Set([...savedCompleted, ...prevLevelLessonIds]));

        // Ensure newly auto-marked previous rank lessons are persisted in DB
        if (prevLevelLessonIds.some(id => !savedCompleted.includes(id))) {
          saveMemberSyllabusProgressDB(activeMember.id, allCompletedIds).catch(() => { });
        }

        const mapped = dbSections.map(sec => {
          const isPrevSection = isPreviousBengkungRank(activeMember.bengkung, sec.bengkungRank);
          return {
            ...sec,
            lessons: sec.lessons.map(les => ({
              ...les,
              completed: isPrevSection || allCompletedIds.includes(les.id)
            }))
          };
        });
        setSections(mapped);

        // Check if activeMember meets requirement for KURSUS TUNAS PENDEKAR (age 7-12, not achieved Putih 0)
        const isTunasEligible = isEligibleForTunasPendekar(activeMember);
        const activeVisibleSections = isTunasEligible ? mapped : mapped.filter(s => !isTunasPendekarSection(s));

        // Auto-open section matching activeMember's current bengkung rank
        const memberDbCode = activeMember.bengkung ? toDbBengkungCode(activeMember.bengkung) : '';
        const currentRankSection = activeVisibleSections.find(s => toDbBengkungCode(s.bengkungRank) === memberDbCode)
          || activeVisibleSections.find(s => isRankUnlocked(s.bengkungRank) && !isPreviousBengkungRank(activeMember.bengkung, s.bengkungRank))
          || activeVisibleSections[0];

        if (currentRankSection) {
          setOpenSectionIds(prev => {
            const filtered = isTunasEligible ? prev : prev.filter(id => !id.startsWith('sec-hitam'));
            return filtered.includes(currentRankSection.id) ? filtered : [...filtered, currentRankSection.id];
          });
        }

        // Set or keep active lesson within accessible lessons
        const accessible = activeVisibleSections.filter(s => isRankUnlocked(s.bengkungRank)).flatMap(s => s.lessons);
        const all = activeVisibleSections.flatMap(s => s.lessons);
        if (accessible.length > 0) {
          setActiveLesson(prev => {
            const existing = accessible.find(l => l.id === prev?.id);
            if (existing) return existing;
            if (currentRankSection && currentRankSection.lessons.length > 0) {
              const incomplete = currentRankSection.lessons.find(l => !l.completed);
              return incomplete || currentRankSection.lessons[0];
            }
            return accessible[0];
          });
        } else if (all.length > 0) {
          setActiveLesson(all[0]);
        }
      } catch {
        setSections(dbSections);
      }
    });
  }, [activeMember?.id, activeMember?.bengkung]);

  // Check if activeMember meets requirement for KURSUS TUNAS PENDEKAR (age 7-12, not achieved Putih 0)
  const isTunasPendekarEligible = isEligibleForTunasPendekar(activeMember);

  // Sections visible specifically for activeMember
  const visibleSections = useMemo(() => {
    if (isTunasPendekarEligible) return sections;
    return sections.filter(s => !isTunasPendekarSection(s));
  }, [sections, isTunasPendekarEligible]);

  // Calculate Course Progression Percentage for currently active learner based on unlocked level
  const allLessons = useMemo(() => visibleSections.flatMap(s => s.lessons), [visibleSections]);

  // Only accessible lessons (unlocked sections for activeMember)
  const accessibleLessons = useMemo(() => {
    return visibleSections
      .filter(s => isRankUnlocked(s.bengkungRank))
      .flatMap(s => s.lessons);
  }, [visibleSections, userBengkungRank, isManagement]);

  // Lessons that count towards the active learner's unlocked level progress
  const targetLessonsForActive = accessibleLessons.length > 0 ? accessibleLessons : allLessons;
  const completedCount = targetLessonsForActive.filter(l => l.completed).length;
  const progressPct = targetLessonsForActive.length > 0 ? Math.round((completedCount / targetLessonsForActive.length) * 100) : 0;

  // Active lesson section and unlocked status
  const activeLessonSection = visibleSections.find(s => s.lessons.some(l => l.id === activeLesson.id)) || sections.find(s => s.lessons.some(l => l.id === activeLesson.id));
  const isActiveLessonUnlocked = !activeLessonSection || isRankUnlocked(activeLessonSection.bengkungRank);

  // Quick navigation indices for Prev / Next Lesson strictly within accessible lessons
  const currentLessonIndex = accessibleLessons.findIndex(l => l.id === activeLesson.id);
  const prevLesson = currentLessonIndex > 0 ? accessibleLessons[currentLessonIndex - 1] : null;
  const nextLesson = currentLessonIndex >= 0 && currentLessonIndex < accessibleLessons.length - 1 ? accessibleLessons[currentLessonIndex + 1] : null;

  // Mobile workspace view toggle: 'curriculum' (default) or 'content' (notes & references)
  const [mobileWorkspaceTab, setMobileWorkspaceTab] = useState<'curriculum' | 'content'>('curriculum');

  // Helper to determine the pesilat's rank category
  const getPesilatRankLevel = (bengkung?: string): 'hitam' | 'putih' | 'merah' | 'kuning' => {
    const b = (bengkung || '').toLowerCase();
    if (b.includes('kuning')) return 'kuning';
    if (b.includes('merah')) return 'merah';
    if (b.includes('putih')) return 'putih';
    return 'hitam';
  };

  // Check if a member rank level encompasses a given syllabus rank (without management override)
  const isMemberRankEligible = (memberBengkung: string | undefined, rank: CourseSection['bengkungRank'] | string): boolean => {
    if (!memberBengkung) return rank.startsWith('hitam') || rank.startsWith('putih');
    const memberIdx = getBengkungRankIndex(memberBengkung);
    const sectionIdx = getBengkungRankIndex(rank);
    if (memberIdx !== -1 && sectionIdx !== -1) {
      return sectionIdx <= memberIdx;
    }
    const b = memberBengkung.toLowerCase();
    if (rank.startsWith('hitam')) return true;
    if (rank.startsWith('putih')) return b.includes('putih') || b.includes('merah') || b.includes('kuning') || b.includes('biru');
    if (rank.startsWith('merah')) return b.includes('merah') || b.includes('kuning');
    if (rank.startsWith('kuning')) return b.includes('kuning');
    return false;
  };

  const activePesilatRank = getPesilatRankLevel(activeMember?.bengkung);
  const currentRankSection = sections.find(s => s.bengkungRank === activePesilatRank) || sections[0];

  // Lessons required specifically for activeMember's rank level to unlock Ujian Bengkung
  const requiredSectionsForMember = visibleSections.filter(s => isMemberRankEligible(activeMember?.bengkung, s.bengkungRank));
  const requiredLessonsForMember = requiredSectionsForMember.flatMap(s => s.lessons);
  const requiredCompletedCount = requiredLessonsForMember.filter(l => l.completed).length;
  const requiredTotalCount = requiredLessonsForMember.length;

  // Whole silibus level is completed if 100% of required lessons are completed
  const isSyllabusRequirementMet = requiredTotalCount > 0 && requiredCompletedCount >= requiredTotalCount;

  // Grading, Ujian Bengkung Pre-Registration & Event State
  const [activeGradingRecord, setActiveGradingRecord] = useState<SyllabusGradingRecord | null>(null);
  const [activeRegistration, setActiveRegistration] = useState<UjianBengkungRegistration | null>(null);
  const [activeEvent, setActiveEvent] = useState<NoticeItem | null>(null);
  const [isPraDaftarModalOpen, setIsPraDaftarModalOpen] = useState(false);
  const [isGradingModalOpen, setIsGradingModalOpen] = useState(false);
  const [gradingNotes, setGradingNotes] = useState('');
  const [gradingRating, setGradingRating] = useState<number>(5);
  const [isSavingGrading, setIsSavingGrading] = useState(false);
  const [isSavingPraDaftar, setIsSavingPraDaftar] = useState(false);

  // Fetch grading record, registration, and active event whenever activeMember or activePesilatRank changes
  const loadGradingAndRegistration = () => {
    if (!activeMember) return;
    getSyllabusGradingDB(activeMember.id, activePesilatRank).then((rec) => {
      setActiveGradingRecord(rec);
      if (rec) {
        setGradingNotes(rec.remark || rec.notes || '');
        setGradingRating(rec.rating || 5);
      } else {
        setGradingNotes('');
        setGradingRating(5);
      }
    }).catch(() => { });
    getMemberUjianBengkungRegistrationDB(activeMember.id).then(setActiveRegistration).catch(() => { });
    getActiveUjianBengkungEventDB().then(setActiveEvent).catch(() => { });
  };

  useEffect(() => {
    loadGradingAndRegistration();
    const handleUpdate = () => loadGradingAndRegistration();
    window.addEventListener('asmm_db_updated', handleUpdate);
    return () => window.removeEventListener('asmm_db_updated', handleUpdate);
  }, [activeMember?.id, activePesilatRank]);

  // Target next bengkung rank for Ujian Kenaikan Bengkung
  const targetNextBengkung = getNextBengkungRank(activeMember?.bengkung || 'Putih');

  // Dual Eligibility Requirement: 100% lessons + Ketua Gelanggang Passing Grade
  const isKetuaGelanggangPassed = activeGradingRecord?.status === 'Lulus';
  const isEligibleForUjian = isSyllabusRequirementMet && isKetuaGelanggangPassed;

  // Top Management authority:
  // "hanya pengerusi, setiausaha, penolong pengerusi saja boleh"
  const isTopManagement = Boolean(
    currentUser && (
      isSuperAdmin(currentUser) ||
      hasRolePermission(currentUser, ['Pengerusi', 'Timbalan Pengerusi', 'Penolong Pengerusi', 'Setiausaha']) ||
      getUserRoles(currentUser).some(r => r.toLowerCase().includes('pengerusi'))
    )
  );

  const isUserSuperAdmin = Boolean(isSuperAdmin(currentUser));

  // Selector visibility: who is allowed to select and evaluate members in the gelanggang panel
  const canSelectStudentsForEvaluation = Boolean(
    currentUser && (
      isTopManagement ||
      hasRolePermission(currentUser, ['Pen. Setiausaha', 'Bendahari', 'Ketua Gelanggang'])
    )
  );

  // Authority to CREATE initial evaluation & remarks on unrated lesson titles:
  // Rule: "superadmin cannot leave remark but able to edit the remark left by ketua gelanggang"
  // Superadmin is excluded from creating initial remarks. Only Ketua Gelanggang and non-superadmin management.
  const canCreateLessonEvaluation = Boolean(
    currentUser && activeMember && !isUserSuperAdmin && (
      currentUser.id !== activeMember.id && (
        activeMember.role === 'Ketua Gelanggang'
          ? isTopManagement
          : (
            isTopManagement ||
            hasRolePermission(currentUser, ['Pen. Setiausaha', 'Bendahari']) ||
            (hasUserRole(currentUser, 'Ketua Gelanggang') && (currentUser.gelanggang === activeMember.gelanggang || currentUser.gelanggang === 'Semua Gelanggang'))
          )
      )
    )
  );

  // Authority to EDIT existing remarks left by Ketua Gelanggang:
  // Rule: "able to edit the remark left by ketua gelanggang"
  // Superadmin CAN ALWAYS edit existing remarks left by Ketua Gelanggang (including on own profile or any student).
  const canEditLessonEvaluation = Boolean(
    currentUser && activeMember && (
      isUserSuperAdmin ||
      (
        currentUser.id !== activeMember.id && (
          activeMember.role === 'Ketua Gelanggang'
            ? isTopManagement
            : (
              isTopManagement ||
              hasRolePermission(currentUser, ['Pen. Setiausaha', 'Bendahari']) ||
              (hasUserRole(currentUser, 'Ketua Gelanggang') && (currentUser.gelanggang === activeMember.gelanggang || currentUser.gelanggang === 'Semua Gelanggang'))
            )
        )
      )
    )
  );

  // Combined flag for evaluation view access
  const canEvaluateActiveMember = canCreateLessonEvaluation || canEditLessonEvaluation;

  const currentGelanggangDetail = gelanggangList.find(g => g.name === activeMember?.gelanggang);
  const ketuaGelanggangName = currentGelanggangDetail?.leaderName || 'Ketua Gelanggang';

  // Relevant members that current user can evaluate - Prioritizes higher management roles first
  const relevantGelanggangMembers = useMemo(() => {
    if (!currentUser) return [];
    if (isTopManagement) {
      // Top management (and Superadmin) can evaluate all active members (including Ketua Gelanggang), but not self
      return members.filter(m => m.status === 'Aktif' && m.id !== currentUser.id);
    }
    if (hasUserRole(currentUser, 'Ketua Gelanggang')) {
      // Ketua Gelanggang cannot evaluate themselves, so filter out self
      return members.filter(m => (m.gelanggang === currentUser.gelanggang || currentUser.gelanggang === 'Semua Gelanggang') && m.status === 'Aktif' && m.id !== currentUser.id);
    }
    if (hasRolePermission(currentUser, ['Pen. Setiausaha', 'Bendahari'])) {
      return members.filter(m => m.status === 'Aktif' && m.id !== currentUser.id && m.role !== 'Ketua Gelanggang');
    }
    return [];
  }, [currentUser, members, isTopManagement]);

  // Pesilat Gelanggang Fuzzy Search Combobox State
  const [isPesilatDropdownOpen, setIsPesilatDropdownOpen] = useState(false);
  const [pesilatSearchQuery, setPesilatSearchQuery] = useState('');
  const pesilatComboboxRef = useRef<HTMLDivElement>(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        pesilatComboboxRef.current &&
        !pesilatComboboxRef.current.contains(e.target as Node)
      ) {
        setIsPesilatDropdownOpen(false);
      }
    };
    if (isPesilatDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPesilatDropdownOpen]);

  const selectedGelanggangMember = useMemo(() => {
    if (!selectedGelanggangMemberId) return null;
    return members.find(m => m.id === selectedGelanggangMemberId) || null;
  }, [selectedGelanggangMemberId, members]);

  // Multi-token fuzzy search across name, bengkung, role, gelanggang, ID, phone
  const filteredGelanggangMembers = useMemo(() => {
    if (!pesilatSearchQuery.trim()) return relevantGelanggangMembers;
    const tokens = pesilatSearchQuery.toLowerCase().trim().split(/\s+/).filter(Boolean);

    return relevantGelanggangMembers.filter((m) => {
      const searchHaystack = [
        m.name || '',
        m.bengkung || '',
        m.role || '',
        m.gelanggang || '',
        m.id || '',
        m.phone || '',
        m.icNo || ''
      ].join(' ').toLowerCase();

      return tokens.every((token) => searchHaystack.includes(token));
    });
  }, [relevantGelanggangMembers, pesilatSearchQuery]);

  const handleSaveGrading = async (status: 'Lulus' | 'Gagal' | 'Belum Dinilai', notes?: string, ratingVal?: number) => {
    if (!activeMember || !currentUser) return;
    setIsSavingGrading(true);
    try {
      const effectiveRating = ratingVal !== undefined ? ratingVal : gradingRating;
      const effectiveRemark = (notes !== undefined ? notes : gradingNotes).trim();
      const record: SyllabusGradingRecord = {
        ...(activeGradingRecord || {}),
        id: `grade-${activeMember.id}-${activePesilatRank}`,
        memberId: activeMember.id,
        rankLevel: activePesilatRank,
        status,
        assessedBy: currentUser.name,
        assessorRole: getHighestRole(currentUser),
        assessedAt: new Date().toISOString(),
        gelanggang: activeMember.gelanggang,
        notes: effectiveRemark || (status === 'Lulus' ? 'Lulus penilaian praktikal silibus peringkat bengkung.' : ''),
        remark: effectiveRemark,
        rating: effectiveRating,
        lessonRatings: activeGradingRecord?.lessonRatings || {}
      };
      await saveSyllabusGradingDB(record);
      setActiveGradingRecord(record);
      setIsGradingModalOpen(false);
      showToast(
        status === 'Lulus' ? 'success' : 'info',
        'Penilaian Berjaya Direkodkan',
        status === 'Lulus'
          ? `Gred LULUS (Penguasaan: ${effectiveRating}/5) disahkan untuk ${formatPublicName(activeMember.name)}. Calon kini layak mendaftar Ujian Bengkung.`
          : `Penilaian penguasaan (${effectiveRating}/5) & ulasan telah disimpan untuk ${formatPublicName(activeMember.name)}.`
      );
    } catch {
      showToast('error', 'Ralat', 'Gagal mengemaskini gred penilaian.');
    } finally {
      setIsSavingGrading(false);
    }
  };

  // Dedicated Evaluation Page View state for individual lesson titles (replaces popup modal)
  const [evaluatingLesson, setEvaluatingLesson] = useState<LessonItem | null>(null);
  const [evalLessonRating, setEvalLessonRating] = useState<number>(5);
  const [evalLessonRemark, setEvalLessonRemark] = useState<string>('');
  const [isSavingLessonEval, setIsSavingLessonEval] = useState(false);
  const previousScrollY = useRef<number>(0);
  const lastEvaluatedLessonIdRef = useRef<string | null>(null);

  const handleOpenLessonEvaluation = (lesson: LessonItem) => {
    previousScrollY.current = window.scrollY;
    lastEvaluatedLessonIdRef.current = lesson.id;
    setEvaluatingLesson(lesson);
    const existing = activeGradingRecord?.lessonRatings?.[lesson.id];
    setEvalLessonRating(existing?.rating || 5);
    setEvalLessonRemark(existing?.remark || '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCloseLessonEvaluation = () => {
    const targetLessonId = evaluatingLesson?.id || lastEvaluatedLessonIdRef.current;

    // Ensure parent section is open so the element is present in DOM
    if (targetLessonId) {
      const parentSection = sections.find(s => s.lessons.some(l => l.id === targetLessonId));
      if (parentSection) {
        setOpenSectionIds(prev => prev.includes(parentSection.id) ? prev : [...prev, parentSection.id]);
      }
    }

    // Ensure mobile view displays Kandungan (curriculum list)
    setMobileWorkspaceTab('curriculum');
    setEvaluatingLesson(null);

    // Scroll back to the lesson title's position
    setTimeout(() => {
      if (targetLessonId) {
        const el = document.getElementById(`syllabus-lesson-${targetLessonId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('ring-2', 'ring-amber-400', 'bg-amber-50/70');
          setTimeout(() => {
            el.classList.remove('ring-2', 'ring-amber-400', 'bg-amber-50/70');
          }, 1600);
          return;
        }
      }
      if (previousScrollY.current > 0) {
        window.scrollTo({ top: previousScrollY.current, behavior: 'smooth' });
      }
    }, 60);
  };

  const handleSwitchEvaluatingLesson = (targetLesson: LessonItem) => {
    lastEvaluatedLessonIdRef.current = targetLesson.id;
    setEvaluatingLesson(targetLesson);
    const existing = activeGradingRecord?.lessonRatings?.[targetLesson.id];
    setEvalLessonRating(existing?.rating || 5);
    setEvalLessonRemark(existing?.remark || '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSaveLessonEvaluation = async (returnToList = true) => {
    if (!activeMember || !currentUser || !evaluatingLesson) return;
    setIsSavingLessonEval(true);
    try {
      const existingRatings = activeGradingRecord?.lessonRatings || {};
      const previousLessonEval = existingRatings[evaluatingLesson.id];
      const assessedByTrainer = previousLessonEval?.assessedBy || activeGradingRecord?.assessedBy || currentUser.name;

      const updatedRatings = {
        ...existingRatings,
        [evaluatingLesson.id]: {
          rating: evalLessonRating,
          remark: evalLessonRemark.trim(),
          assessedAt: new Date().toISOString(),
          assessedBy: assessedByTrainer
        }
      };

      const record: SyllabusGradingRecord = {
        ...(activeGradingRecord || {
          id: `grade-${activeMember.id}-${activePesilatRank}`,
          memberId: activeMember.id,
          rankLevel: activePesilatRank,
          status: 'Belum Dinilai',
          gelanggang: activeMember.gelanggang,
          notes: '',
          remark: '',
          rating: gradingRating
        }),
        assessedBy: activeGradingRecord?.assessedBy || (isUserSuperAdmin ? (ketuaGelanggangName || 'Ketua Gelanggang') : currentUser.name),
        assessorRole: activeGradingRecord?.assessorRole || (isUserSuperAdmin ? 'Ketua Gelanggang' : getHighestRole(currentUser)),
        assessedAt: new Date().toISOString(),
        lessonRatings: updatedRatings
      };

      await saveSyllabusGradingDB(record);
      setActiveGradingRecord(record);
      showToast('success', 'Penilaian & Remark Dikemaskini!', `Tahap ${evalLessonRating}/5 & ulasan untuk "${evaluatingLesson.title}" telah disimpan.`);

      if (returnToList) {
        handleCloseLessonEvaluation();
      }
    } catch {
      showToast('error', 'Ralat', 'Gagal menyimpan penilaian pelajaran.');
    } finally {
      setIsSavingLessonEval(false);
    }
  };

  const handleConfirmPraDaftar = async () => {
    if (!activeMember) return;
    setIsSavingPraDaftar(true);
    try {
      const cleanCurrent = cleanBengkungTitle(activeMember.bengkung || 'Putih');
      const cleanTarget = cleanBengkungTitle(targetNextBengkung);
      const reg: UjianBengkungRegistration = {
        id: `ub-reg-${Date.now()}`,
        memberId: activeMember.id,
        memberName: activeMember.name,
        memberPhone: activeMember.phone,
        memberIcNo: activeMember.icNo,
        gelanggang: activeMember.gelanggang,
        currentBengkung: cleanCurrent,
        targetBengkung: cleanTarget,
        status: 'pra-daftar',
        registeredAt: new Date().toISOString(),
        notes: 'Pra-pendaftaran nama calon tanpa bayaran awal kerana tarikh tahunan belum tetap.'
      };
      await saveUjianBengkungRegistrationDB(reg);
      setActiveRegistration(reg);
      setIsPraDaftarModalOpen(false);
      showToast(
        'success',
        'Pra-Pendaftaran Berjaya!',
        `Nama ${formatPublicName(activeMember.name)} telah direkodkan dalam senarai menunggu calon Ujian Bengkung. Tiada bayaran dikenakan buat masa ini.`
      );
    } catch {
      showToast('error', 'Ralat', 'Gagal merekodkan pra-pendaftaran.');
    } finally {
      setIsSavingPraDaftar(false);
    }
  };


  // Helper to compute progress info for any member ID based on their already unlocked level
  const getMemberProgressInfo = (memberId: string) => {
    const member = members.find(m => m.id === memberId) || (currentUser?.id === memberId ? currentUser : null);
    const memberTunasEligible = isEligibleForTunasPendekar(member);
    const memberSections = memberTunasEligible ? sections : sections.filter(s => !isTunasPendekarSection(s));

    // Lessons in sections that this member has already unlocked
    const memberUnlockedSections = memberSections.filter(s => isMemberRankEligible(member?.bengkung, s.bengkungRank));
    const memberUnlockedLessons = memberUnlockedSections.flatMap(s => s.lessons);
    const targetLessons = memberUnlockedLessons.length > 0 ? memberUnlockedLessons : memberSections.flatMap(s => s.lessons);
    const targetLessonIds = new Set(targetLessons.map(l => l.id));

    // Combine manually saved completions with auto-marked previous rank lessons
    const prevRankLessonIds = getPreviousRankLessonIds(member?.bengkung, memberSections);
    const combinedCompletedSet = new Set([...(familyProgressMap[memberId] || []), ...prevRankLessonIds]);

    const completedList = Array.from(combinedCompletedSet).filter(id => targetLessonIds.has(id));
    const totalCount = targetLessons.length;
    const count = completedList.length;
    const pct = totalCount > 0 ? Math.round((count / totalCount) * 100) : 0;
    return { count, totalCount, pct };
  };

  // Real-time completion status of the active lesson derived from sections state
  const currentLessonFromSections = allLessons.find(l => l.id === activeLesson.id);
  const isCurrentLessonCompleted = currentLessonFromSections ? currentLessonFromSections.completed : activeLesson.completed;

  const toggleLessonComplete = async (lessonId: string, sectionRank?: CourseSection['bengkungRank'] | string) => {
    if (!activeMember) return;

    // Detect section rank if not provided
    const targetSection = sections.find(s => s.lessons.some(l => l.id === lessonId));
    const effectiveRank = sectionRank || targetSection?.bengkungRank || 'hitam';

    if (!isRankUnlocked(effectiveRank)) {
      showToast('warning', 'Akses Modul Terkunci', 'Sila lulus Ujian Kenaikan Bengkung untuk membuka modul ini.');
      return;
    }

    if (isPreviousBengkungRank(activeMember?.bengkung, effectiveRank)) {
      showToast('info', 'Tahap Selesai', 'Pelajaran peringkat bengkung terdahulu telah disahkan selesai.');
      return;
    }

    let nextCompletedState = false;
    const nextSections = sections.map(s => ({
      ...s,
      lessons: s.lessons.map(l => {
        if (l.id === lessonId) {
          nextCompletedState = !l.completed;
          return { ...l, completed: nextCompletedState };
        }
        return l;
      })
    }));

    setSections(nextSections);

    // CRITICAL: Synchronize activeLesson state immediately so UI re-renders with the new completed state
    setActiveLesson(prev => {
      if (prev && prev.id === lessonId) {
        return { ...prev, completed: nextCompletedState };
      }
      return prev;
    });

    // Save completed lesson IDs specifically for activeMember
    const nextCompleted = nextSections.flatMap(s => s.lessons).filter(l => l.completed).map(l => l.id);
    await saveMemberSyllabusProgressDB(activeMember.id, nextCompleted);

    // Update family progress map for instant reactive UI updates
    setFamilyProgressMap(prev => ({
      ...prev,
      [activeMember.id]: nextCompleted
    }));
  };

  const handleDeleteLesson = (lessonId: string, lessonTitle: string) => {
    setConfirmDeleteLessonId({ id: lessonId, title: lessonTitle });
  };

  const confirmDeleteLesson = async () => {
    if (!confirmDeleteLessonId) return;
    const updated = sections.map(sec => ({
      ...sec,
      lessons: sec.lessons.filter(l => l.id !== confirmDeleteLessonId.id)
    }));
    setSections(updated);
    await saveSyllabusDB(updated);
    showToast('info', 'Pelajaran Dipadam', confirmDeleteLessonId.title);

    if (activeLesson.id === confirmDeleteLessonId.id) {
      const remaining = updated.flatMap(s => s.lessons);
      if (remaining.length > 0) {
        setActiveLesson(remaining[0]);
      }
    }
    setConfirmDeleteLessonId(null);
  };

  const handleAddLesson = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newLessonTitle.trim() || !newLessonDescription.trim()) {
      showToast('error', 'Sila isi tajuk dan penerangan pelajaran.');
      return;
    }

    const newLesson: LessonItem = {
      id: `les-${Date.now()}`,
      title: newLessonTitle,
      type: newLessonType,
      duration: newLessonType === 'pdf' ? 'Fail PDF' : newLessonDuration,
      completed: false,
      description: newLessonDescription
    };

    const updated = sections.map(sec => {
      if (sec.bengkungRank === newLessonTargetRank) {
        return {
          ...sec,
          lessons: [...sec.lessons, newLesson]
        };
      }
      return sec;
    });

    setSections(updated);
    await saveSyllabusDB(updated);
    showToast('success', 'Pelajaran Baru Ditambah!', newLessonTitle);
    setIsAddLessonModalOpen(false);

    // Reset Form
    setNewLessonTitle('');
    setNewLessonDescription('');
  };

  const handleSelectLesson = (lesson: LessonItem, sectionRank: CourseSection['bengkungRank'] | string, reqName: string) => {
    if (!isRankUnlocked(sectionRank)) {
      showToast(
        'warning',
        `Akses Terhad (${reqName})`,
        `Pelajaran "${lesson.title}" hanya boleh diakses setelah lulus ${reqName}. Modul seterusnya terkunci sehingga kelulusan Ujian Bengkung.`
      );
      return;
    }
    const parentSection = sections.find(s => s.lessons.some(l => l.id === lesson.id));
    if (parentSection) {
      setOpenSectionIds(prev => prev.includes(parentSection.id) ? prev : [...prev, parentSection.id]);
    }
    setIsPlayingVideo(false);
    setActiveLesson(lesson);
    if (window.innerWidth < 768) {
      setMobileWorkspaceTab('content');
      document.getElementById('syllabus-player-workspace')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Edit lesson handlers
  const handleOpenEditLesson = (lesson: LessonItem, sectionId: string) => {
    setEditLessonData({ lesson, sectionId });
    setEditTitle(lesson.title);
    setEditType(lesson.type);
    setEditDuration(lesson.duration);
    setEditDescription(lesson.description);
    setEditVideoUrl(lesson.videoUrl || '');
    setEditPdfFileId(lesson.pdfFileId || '');
    setIsEditLessonModalOpen(true);
  };

  const handleSaveEditLesson = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editLessonData || !editTitle.trim() || !editDescription.trim()) {
      showToast('error', 'Sila isi tajuk dan penerangan pelajaran.');
      return;
    }

    try {
      await updateLessonDB(editLessonData.sectionId, editLessonData.lesson.id, {
        title: editTitle,
        type: editType,
        duration: editType === 'pdf' ? 'Fail PDF' : editDuration,
        description: editDescription,
        videoUrl: editVideoUrl || undefined,
        pdfFileId: editPdfFileId || undefined,
      });

      // Refresh sections data
      const dbSections = await getSyllabusDB();
      setSections(dbSections);

      // Update active lesson if it was the one edited
      if (activeLesson.id === editLessonData.lesson.id) {
        setActiveLesson(prev => ({
          ...prev,
          title: editTitle,
          type: editType,
          duration: editType === 'pdf' ? 'Fail PDF' : editDuration,
          description: editDescription,
          videoUrl: editVideoUrl || undefined,
          pdfFileId: editPdfFileId || undefined,
        }));
      }

      showToast('success', 'Pelajaran Dikemaskini!', editTitle);
      setIsEditLessonModalOpen(false);
      setEditLessonData(null);
    } catch {
      showToast('error', 'Gagal mengemaskini pelajaran.');
    }
  };

  // Dedicated Full Page View for Nilai & Remark Tajuk Silibus (Simpler, Stars-Only, Minimal Text)
  if (evaluatingLesson && activeMember && canEvaluateActiveMember) {
    const currentEvalIndex = allLessons.findIndex(l => l.id === evaluatingLesson.id);
    const prevEvalLesson = currentEvalIndex > 0 ? allLessons[currentEvalIndex - 1] : null;
    const nextEvalLesson = currentEvalIndex >= 0 && currentEvalIndex < allLessons.length - 1 ? allLessons[currentEvalIndex + 1] : null;

    return (
      <div className="w-full max-w-xl mx-auto space-y-3.5 min-h-[60vh] animate-fadeIn pb-12">
        {/* Top Bar: Back & Quick Navigation */}
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={handleCloseLessonEvaluation}
            className="px-3 py-1.5 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-bold flex items-center gap-1.5 shadow-2xs cursor-pointer transition-all active:scale-95"
          >
            <ArrowLeft className="w-3.5 h-3.5 text-slate-500" />
            <span>Kembali</span>
          </button>

          {/* Quick Prev / Next Pagination */}
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              disabled={!prevEvalLesson}
              onClick={() => prevEvalLesson && handleSwitchEvaluatingLesson(prevEvalLesson)}
              className={`p-1.5 rounded-lg border text-xs font-bold transition-all ${prevEvalLesson
                ? 'bg-white hover:bg-slate-50 text-slate-700 border-slate-200 cursor-pointer active:scale-95'
                : 'bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed'
                }`}
              title={prevEvalLesson ? prevEvalLesson.title : undefined}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-[11px] font-mono font-bold text-slate-500 px-2">
              {currentEvalIndex >= 0 ? currentEvalIndex + 1 : 1} / {allLessons.length}
            </span>
            <button
              type="button"
              disabled={!nextEvalLesson}
              onClick={() => nextEvalLesson && handleSwitchEvaluatingLesson(nextEvalLesson)}
              className={`p-1.5 rounded-lg border text-xs font-bold transition-all ${nextEvalLesson
                ? 'bg-white hover:bg-slate-50 text-slate-700 border-slate-200 cursor-pointer active:scale-95'
                : 'bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed'
                }`}
              title={nextEvalLesson ? nextEvalLesson.title : undefined}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Minimalist Card */}
        <div className="bg-white p-5 sm:p-6 rounded-2xl border border-slate-200 shadow-sm space-y-5">
          {/* Header Info */}
          <div className="text-center space-y-1 pb-2 border-b border-slate-100">
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <span className="text-[11px] font-bold text-asmm-red">
                {activeMember.name} • {activeMember.bengkung || 'Bengkung Putih'}
              </span>
              {isUserSuperAdmin && (
                <span className="text-[10px] font-extrabold bg-amber-100 text-amber-900 border border-amber-300 px-2 py-0.5 rounded-full">
                  Kemaskini Ulasan Ketua Gelanggang
                </span>
              )}
            </div>
            <h2 className="text-base sm:text-lg font-black text-slate-900 leading-snug">
              {evaluatingLesson.title}
            </h2>
          </div>

          {/* Stars Only for Rate Scale */}
          <div className="flex flex-col items-center justify-center py-2 space-y-2">
            <div className="flex items-center justify-center gap-2 sm:gap-3">
              {[1, 2, 3, 4, 5].map((star) => {
                const isFilled = star <= evalLessonRating;
                return (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setEvalLessonRating(star)}
                    className="p-1 cursor-pointer transition-transform hover:scale-125 active:scale-95 focus:outline-none"
                    title={`${star} Bintang`}
                  >
                    <Star
                      className={`w-9 h-9 sm:w-11 sm:h-11 transition-colors ${isFilled
                        ? 'fill-amber-400 text-amber-400 drop-shadow-xs'
                        : 'text-slate-200 hover:text-amber-200'
                        }`}
                    />
                  </button>
                );
              })}
            </div>
            <span className="text-xs font-extrabold text-amber-700 font-mono">
              {evalLessonRating} / 5 Bintang
            </span>
          </div>

          {/* Remark Textarea */}
          <div className="space-y-1.5">
            <label className="block text-xs font-bold text-slate-700">
              Remark / Ulasan (Pilihan):
            </label>
            <textarea
              rows={3}
              value={evalLessonRemark}
              onChange={(e) => setEvalLessonRemark(e.target.value)}
              placeholder="Tulis ulasan pembetulan untuk pesilat..."
              className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white text-xs text-slate-900 outline-none leading-relaxed transition-all"
            />
          </div>

          {/* Short Quick Chips */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {[
              'Kemas & berpadu',
              'Perbaiki kekuda',
              'Ulang latihan jurus',
              'Langkah terpuji'
            ].map((chipText, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setEvalLessonRemark(chipText)}
                className="text-[11px] px-2.5 py-1 bg-slate-100 hover:bg-amber-50 hover:text-amber-900 text-slate-600 rounded-lg border border-slate-200 cursor-pointer transition-all active:scale-95 font-medium"
              >
                + {chipText}
              </button>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
            <button
              type="button"
              onClick={handleCloseLessonEvaluation}
              className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-600 hover:text-slate-900 cursor-pointer"
            >
              Batal
            </button>
            <button
              type="button"
              onClick={() => handleSaveLessonEvaluation(true)}
              disabled={isSavingLessonEval}
              className="px-5 py-2.5 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold text-xs rounded-xl transition-all shadow-sm flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
            >
              <Check className="w-4 h-4" />
              <span>{isSavingLessonEval ? 'Menyimpan...' : 'Simpan Remark'}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 min-h-[75vh]">
      {/* Fixed Header Banner - White Background matching all pages */}
      <PageHeader
        badgeText="Kurikulum"
        badgeIcon={BookOpen}
        title="Pengajian Silibus"
        titleBadge={allLessons.length > 0 ? `${allLessons.length} Pelajaran` : undefined}
      >
        {isManagement && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                window.history.pushState(null, '', '/pengurusan-silibus');
                window.dispatchEvent(new PopStateEvent('popstate'));
              }}
              className="px-3 sm:px-3.5 py-1.5 sm:py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl shadow-xs transition-all flex items-center gap-1.5 shrink-0 cursor-pointer"
            >
              <Settings className="w-3.5 h-3.5" />
              <span>Pengurusan Silibus</span>
            </button>
            <button
              onClick={() => setIsAddLessonModalOpen(true)}
              className="px-3 sm:px-3.5 py-1.5 sm:py-2 bg-asmm-red hover:bg-asmm-crimson text-white text-xs font-bold rounded-xl shadow-xs transition-all flex items-center gap-1.5 shrink-0 cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Tambah Pelajaran</span>
            </button>
          </div>
        )}
      </PageHeader>

      {/* MULTI-USER / AHLI KELUARGA / GELANGGANG LEARNER SELECTOR BAR */}
      {currentUser && (hasFamily || canUserAddChild(currentUser) || canSelectStudentsForEvaluation) && (
        <div className="bg-white p-4 sm:p-5 rounded-2xl sm:rounded-3xl border border-slate-200 shadow-sm space-y-3 sm:space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-3 border-b border-slate-100 pb-3">
            <div className="flex items-center gap-2.5 sm:gap-3">
              <div className="p-2 sm:p-2.5 bg-red-50 text-asmm-red rounded-xl sm:rounded-2xl border border-red-100 shadow-2xs shrink-0">
                <Users className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-xs sm:text-base font-extrabold text-slate-900">
                    {isCurrentUserPesilat ? 'Pengajian Silibus Ahli & Keluarga' : 'Pengajian Silibus Pesilat (Tanggungan)'}
                  </h3>
                  <span className="text-[9px] sm:text-[10px] font-black bg-red-100 text-asmm-red px-2 sm:px-2.5 py-0.5 rounded-full">
                    {pesilatLearners.length} Pesilat
                  </span>
                </div>
                <p className="text-[11px] sm:text-xs text-slate-500 font-medium">
                  {isCurrentUserPesilat
                    ? 'Pilih pesilat untuk melihat silibus mengikut bengkung dan rekod kemajuan.'
                    : 'Pilih anak / tanggungan pesilat untuk memantau silibus dan kemajuan latihan.'}
                </p>
              </div>
            </div>
          </div>

          {/* Learner Selection Cards - Fullwidth if single pesilat, sideways scroll on mobile & grid on desktop if multiple */}
          <div className={
            isSinglePesilat
              ? "w-full"
              : "flex sm:grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5 sm:gap-3 overflow-x-auto pb-1 sm:pb-0 scrollbar-none snap-x"
          }>
            {/* Parent / Self Learner Option - ONLY rendered for active pesilat, NOT for non-pesilat waris */}
            {isCurrentUserPesilat && (
              <div
                onClick={() => setSelectedChildId(null)}
                className={`p-3 sm:p-3.5 rounded-2xl border transition-all cursor-pointer relative flex flex-col justify-between gap-2.5 sm:gap-3 ${isSinglePesilat
                  ? "w-full"
                  : "shrink-0 sm:shrink min-w-[210px] sm:min-w-0 snap-start"
                  } ${activeMember?.id === currentUser.id
                    ? 'bg-red-50/80 border-asmm-red ring-2 ring-asmm-red/30 shadow-sm'
                    : 'bg-slate-50/70 hover:bg-white border-slate-200 hover:border-slate-300'
                  }`}
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-slate-900 text-white font-black text-xs flex items-center justify-center shrink-0 border border-white shadow-xs overflow-hidden">
                    <img
                      src={currentUser.photoUrl || '/default-avatar.svg'}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={(e) => { (e.target as HTMLImageElement).src = '/default-avatar.svg'; }}
                    />
                  </div>
                  <div className="overflow-hidden flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-bold text-slate-900 truncate">{formatPublicName(currentUser.name)}</span>
                      <span className="text-[9px] font-extrabold bg-slate-200 text-slate-700 px-1.5 py-0.2 rounded shrink-0">
                        Diri Sendiri
                      </span>
                    </div>
                    <div className="text-[10px] text-slate-500 font-medium truncate font-mono">
                      {sanitizeMemberId(currentUser.id)} • {currentUser.bengkung || 'Bengkung Hitam'}
                    </div>
                  </div>
                </div>

                {/* Individual Member Progress Bar */}
                {(() => {
                  const p = getMemberProgressInfo(currentUser.id);
                  return (
                    <div className="space-y-1 pt-1 border-t border-slate-200/60">
                      <div className="flex items-center justify-between text-[10px] font-bold">
                        <span className="text-slate-500">Kemajuan Silibus</span>
                        <span className="text-asmm-red font-mono">{p.pct}% ({p.count}/{p.totalCount})</span>
                      </div>
                      <div className="w-full bg-slate-200 h-1.5 rounded-full overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-amber-500 to-emerald-500 h-full transition-all duration-300"
                          style={{ width: `${p.pct}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* If Waris non-pesilat has no children yet */}
            {!isCurrentUserPesilat && familyMembers.length === 0 && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl text-xs text-amber-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 col-span-full">
                <span>Sebagai Waris (bukan pesilat), anda perlu mendaftarkan anak/tanggungan pesilat untuk melihat silibus latihan.</span>
                <button
                  type="button"
                  onClick={() => {
                    window.history.pushState(null, '', '/keluarga');
                    window.dispatchEvent(new PopStateEvent('popstate'));
                  }}
                  className="px-3.5 py-1.5 bg-asmm-red hover:bg-red-700 text-white font-bold rounded-xl shadow-xs transition-colors shrink-0"
                >
                  Daftar Anak Pesilat
                </button>
              </div>
            )}

            {/* Family Members (Children / Dependents) */}
            {familyMembers.map((child) => {
              const isSelected = activeMember?.id === child.id;
              const p = getMemberProgressInfo(child.id);

              return (
                <div
                  key={child.id}
                  onClick={() => setSelectedChildId(child.id)}
                  className={`p-3 sm:p-3.5 rounded-2xl border transition-all cursor-pointer relative flex flex-col justify-between gap-2.5 sm:gap-3 ${isSinglePesilat
                    ? "w-full"
                    : "shrink-0 sm:shrink min-w-[210px] sm:min-w-0 snap-start"
                    } ${isSelected
                      ? 'bg-red-50/80 border-asmm-red ring-2 ring-asmm-red/30 shadow-sm'
                      : 'bg-slate-50/70 hover:bg-white border-slate-200 hover:border-slate-300'
                    }`}
                >
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-asmm-red text-white font-black text-xs flex items-center justify-center shrink-0 border border-white shadow-xs overflow-hidden">
                      <img
                        src={child.photoUrl || '/default-avatar.svg'}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => { (e.target as HTMLImageElement).src = '/default-avatar.svg'; }}
                      />
                    </div>
                    <div className="overflow-hidden flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-bold text-slate-900 truncate">{formatPublicName(child.name)}</span>
                        <span className="text-[9px] font-extrabold bg-red-100 text-asmm-red px-1.5 py-0.2 rounded shrink-0">
                          {child.relationship || 'Anak'}
                        </span>
                      </div>
                      <div className="text-[10px] text-slate-500 font-medium truncate font-mono">
                        {sanitizeMemberId(child.id)} • {child.bengkung || 'Bengkung Hitam'}
                      </div>
                    </div>
                  </div>

                  {/* Child Progress Bar */}
                  <div className="space-y-1 pt-1 border-t border-slate-200/60">
                    <div className="flex items-center justify-between text-[10px] font-bold">
                      <span className="text-slate-500">Kemajuan Silibus</span>
                      <span className="text-asmm-red font-mono">{p.pct}% ({p.count}/{p.totalCount})</span>
                    </div>
                    <div className="w-full bg-slate-200 h-1.5 rounded-full overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-amber-500 to-emerald-500 h-full transition-all duration-300"
                        style={{ width: `${p.pct}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Ketua Gelanggang / Pengurusan: Pemilihan Pesilat Gelanggang Untuk Penilaian */}
          {canSelectStudentsForEvaluation && relevantGelanggangMembers.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-200/70 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 bg-slate-50/80 p-3 rounded-2xl border border-slate-200 min-w-0 relative z-20">
              <div className="flex items-center gap-2 min-w-0">
                <div className="p-1.5 bg-slate-900 text-white rounded-lg">
                  <ShieldCheck className="w-4 h-4 text-amber-400" />
                </div>
                <div className="min-w-0">
                  <span className="text-xs font-extrabold text-slate-900 block truncate">
                    {currentUser?.role === 'Ketua Gelanggang'
                      ? `Penilaian Pesilat Gelanggang (${currentUser.gelanggang})`
                      : 'Penilaian Pesilat Gelanggang (Pengurusan)'}
                  </span>
                  <span className="text-[10px] text-slate-500 font-medium truncate block">
                    Pilih pesilat untuk menyemak kemajuan pelajaran & mengesahkan gred kelayakan ujian bengkung.
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto min-w-0">
                <div ref={pesilatComboboxRef} className="relative flex-1 min-w-0 sm:w-72 md:w-80">
                  {/* Combobox Trigger Button */}
                  <button
                    type="button"
                    onClick={() => setIsPesilatDropdownOpen(prev => !prev)}
                    className={`w-full min-w-0 max-w-full px-3 py-2 bg-white border rounded-xl text-xs font-bold text-slate-900 shadow-2xs flex items-center justify-between gap-2 transition-all cursor-pointer ${isPesilatDropdownOpen ? 'border-asmm-red ring-2 ring-red-100' : 'border-slate-300 hover:border-slate-400'
                      }`}
                  >
                    <div className="flex items-center gap-2 min-w-0 truncate">
                      <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      {selectedGelanggangMember ? (
                        <span className="text-slate-900 font-extrabold truncate text-left">
                          {selectedGelanggangMember.name}
                        </span>
                      ) : (
                        <span className="text-slate-500 font-medium truncate">
                          -- Cari / Pilih Pesilat ({relevantGelanggangMembers.length}) --
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      {selectedGelanggangMember && (
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedGelanggangMemberId(null);
                            setPesilatSearchQuery('');
                          }}
                          className="p-1 hover:bg-rose-50 text-slate-400 hover:text-rose-600 rounded-md transition-colors cursor-pointer flex items-center justify-center"
                          title="Batal pilihan pesilat"
                        >
                          <X className="w-3.5 h-3.5" />
                        </span>
                      )}
                      <ChevronDown
                        className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${isPesilatDropdownOpen ? 'rotate-180 text-asmm-red' : ''
                          }`}
                      />
                    </div>
                  </button>

                  {/* Dropdown Menu with Search Input */}
                  {isPesilatDropdownOpen && (
                    <div className="absolute z-50 left-0 right-0 sm:right-auto sm:w-96 mt-1.5 bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden animate-fadeIn">
                      {/* Search Input Bar */}
                      <div className="p-2.5 bg-slate-50/90 border-b border-slate-200/80">
                        <div className="relative">
                          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                          <input
                            type="text"
                            autoFocus
                            value={pesilatSearchQuery}
                            onChange={(e) => setPesilatSearchQuery(e.target.value)}
                            placeholder="Cari nama pesilat, bengkung, gelanggang..."
                            className="w-full pl-8 pr-7 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-semibold text-slate-900 focus:outline-none focus:border-asmm-red focus:ring-1 focus:ring-red-100 placeholder:text-slate-400"
                          />
                          {pesilatSearchQuery && (
                            <button
                              type="button"
                              onClick={() => setPesilatSearchQuery('')}
                              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-600 rounded cursor-pointer"
                              title="Padam carian"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Members List */}
                      <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
                        {selectedGelanggangMemberId && (
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedGelanggangMemberId(null);
                              setIsPesilatDropdownOpen(false);
                              setPesilatSearchQuery('');
                            }}
                            className="w-full px-3.5 py-2 text-left text-xs font-bold text-rose-600 hover:bg-rose-50/60 flex items-center justify-between transition-colors cursor-pointer"
                          >
                            <span>✕ Nyahpilih (Kembali ke profil asal)</span>
                          </button>
                        )}

                        {filteredGelanggangMembers.length > 0 ? (
                          filteredGelanggangMembers.map((m) => {
                            const isSelected = m.id === selectedGelanggangMemberId;
                            return (
                              <button
                                key={m.id}
                                type="button"
                                onClick={() => {
                                  setSelectedGelanggangMemberId(m.id);
                                  setIsPesilatDropdownOpen(false);
                                  setPesilatSearchQuery('');
                                }}
                                className={`w-full px-3.5 py-2.5 text-left text-xs transition-colors flex items-center justify-between gap-2 cursor-pointer ${isSelected
                                  ? 'bg-amber-50/80 text-amber-950 font-bold'
                                  : 'hover:bg-slate-50 text-slate-800'
                                  }`}
                              >
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="font-extrabold text-slate-900 truncate">
                                      {m.name}
                                    </span>
                                    <span className="text-[10px] font-mono font-bold text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">
                                      {sanitizeMemberId(m.id)}
                                    </span>
                                    <span className="text-[10px] font-bold text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">
                                      {m.bengkung || 'Bengkung Hitam'}
                                    </span>
                                  </div>
                                  <div className="text-[10px] text-slate-400 font-medium mt-0.5 flex items-center gap-2">
                                    <span>{m.gelanggang}</span>
                                    {m.role && m.role !== 'Ahli' && (
                                      <span>• {m.role}</span>
                                    )}
                                  </div>
                                </div>

                                {isSelected ? (
                                  <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                                ) : (
                                  <span className="text-[10px] text-slate-400">Pilih</span>
                                )}
                              </button>
                            );
                          })
                        ) : (
                          <div className="py-6 px-4 text-center">
                            <p className="text-xs text-slate-500 font-medium">
                              Tiada pesilat ditemui untuk "{pesilatSearchQuery}"
                            </p>
                            <button
                              type="button"
                              onClick={() => setPesilatSearchQuery('')}
                              className="mt-1.5 text-[11px] font-bold text-asmm-red hover:underline cursor-pointer"
                            >
                              Padam carian
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Footer count indicator */}
                      <div className="px-3 py-1.5 bg-slate-50 border-t border-slate-100 text-[10px] text-slate-400 font-medium flex items-center justify-between">
                        <span>Menunjukkan {filteredGelanggangMembers.length} daripada {relevantGelanggangMembers.length} pesilat</span>
                        <span>Fuzzy Search</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* KETUA GELANGGANG EVALUATION & GRADING CONTROL BAR */}
      {canEvaluateActiveMember && activeMember && (
        <div className="p-4 rounded-2xl border transition-all duration-300 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs bg-slate-900 text-white border-slate-800 min-w-0 overflow-hidden">
          <div className="flex items-start sm:items-center gap-3 min-w-0 flex-1">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border shadow-xs ${isKetuaGelanggangPassed
              ? 'bg-emerald-600 text-white border-emerald-500 ring-2 ring-emerald-500/30'
              : 'bg-amber-500 text-slate-950 border-amber-400 ring-2 ring-amber-400/30'
              }`}>
              <Award className="w-5 h-5 shrink-0" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                <span className="font-extrabold text-sm text-white break-words">
                  {currentUser?.role === 'Ketua Gelanggang'
                    ? `Penilaian Ketua Gelanggang (${currentUser.name})`
                    : `Pengesahan Penilaian (${currentUser?.role}: ${currentUser?.name})`}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider shrink-0 ${isKetuaGelanggangPassed
                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                  : 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                  }`}>
                  {isKetuaGelanggangPassed ? 'GRED: LULUS' : 'GRED: BELUM DINILAI / BELUM LULUS'}
                </span>

                {activeGradingRecord?.rating && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-black bg-amber-400/20 text-amber-300 border border-amber-400/30 shrink-0">
                    <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                    <span>Penguasaan: {activeGradingRecord.rating}/5 ({RATING_LEVELS.find(r => r.value === activeGradingRecord.rating)?.label.split(':')[1]?.trim() || ''})</span>
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-300 leading-relaxed break-words">
                Calon: <strong className="text-white">{activeMember.name}</strong> ({activeMember.bengkung || 'Bengkung Hitam'}) • Kemajuan: <strong className="text-amber-300">{requiredCompletedCount}/{requiredTotalCount} Pelajaran ({progressPct}%)</strong>
                {activeGradingRecord && activeGradingRecord.assessedAt && (
                  <span className="block text-[10px] text-slate-400 mt-0.5 break-words">
                    Disahkan oleh {activeGradingRecord.assessedBy} ({activeGradingRecord.assessorRole}) pada {activeGradingRecord.assessedAt.split('T')[0]}
                  </span>
                )}
              </p>

              {/* Remark / Ulasan Box */}
              {(activeGradingRecord?.remark || activeGradingRecord?.notes) && (
                <div className="mt-2 p-2.5 bg-slate-800/90 rounded-xl border border-slate-700/80 text-[11px] text-amber-100 flex items-start gap-2 break-words">
                  <MessageSquare className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-extrabold text-amber-300">Remark / Ulasan: </span>
                    <span className="italic">"{activeGradingRecord.remark || activeGradingRecord.notes}"</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
            <button
              type="button"
              onClick={() => {
                setGradingRating(activeGradingRecord?.rating || 5);
                setGradingNotes(activeGradingRecord?.remark || activeGradingRecord?.notes || '');
                setIsGradingModalOpen(true);
              }}
              className="w-full sm:w-auto px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs rounded-xl transition-all shadow-md flex items-center justify-center gap-1.5 cursor-pointer text-center"
            >
              <Sparkles className="w-3.5 h-3.5 text-amber-300 shrink-0" />
              <span>{isKetuaGelanggangPassed ? 'Kemaskini Gred & Ulasan' : 'Beri Penilaian (Skala 1-5) & Gred'}</span>
            </button>
          </div>
        </div>
      )}

      {/* STATE 1: Pelajaran 100%, tetapi belum ada Gred Lulus Ketua Gelanggang */}
      {(!isManagement || !showManagementPanel) && isSyllabusRequirementMet && !isKetuaGelanggangPassed && (
        <div className="p-3.5 sm:p-4 rounded-2xl border transition-all duration-300 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 text-xs bg-gradient-to-r from-amber-50 via-orange-50/50 to-amber-50 border-amber-300 animate-fadeIn">
          <div className="flex items-start sm:items-center gap-3 text-slate-800 font-medium min-w-0 flex-1">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 border bg-amber-500 text-white border-amber-400 shadow-xs ring-2 ring-amber-100">
              <Clock className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                <span className="font-extrabold text-slate-900 text-xs sm:text-sm">
                  {activeMember?.role === 'Ketua Gelanggang'
                    ? 'Pelajaran Selesai (100%) — Menunggu Gred Lulus Pengurusan Tertinggi'
                    : 'Pelajaran Selesai (100%) — Menunggu Gred Lulus Ketua Gelanggang'}
                </span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-black border uppercase tracking-wider bg-amber-100 text-amber-900 border-amber-300">
                  {requiredCompletedCount}/{requiredTotalCount} Pelajaran
                </span>
              </div>
              <p className="text-[11px] text-slate-600 leading-relaxed">
                {activeMember?.role === 'Ketua Gelanggang' ? (
                  <>
                    Sebagai <strong>Ketua Gelanggang</strong>, penilaian praktikal silibus anda tidak boleh disahkan sendiri. Kelayakan ujian bengkung anda mesti disahkan oleh <strong>Pengerusi, Timbalan Pengerusi (Penolong Pengerusi), atau Setiausaha</strong>.
                  </>
                ) : (
                  <>
                    Tahniah <strong>{activeMember ? formatPublicName(activeMember.name) : 'pesilat'}</strong>! Anda telah melengkapkan seluruh pelajaran silibus tahap ini. Untuk melayakkan pendaftaran Ujian Bengkung, sila jalani penilaian praktikal bersama <strong>Ketua Gelanggang ({ketuaGelanggangName})</strong> untuk mendapatkan pengesahan <strong>Gred Lulus (*Passing Grade*)</strong>.
                  </>
                )}
              </p>

              {/* Assessment Feedback from Ketua Gelanggang if available */}
              {activeGradingRecord && (activeGradingRecord.rating || activeGradingRecord.remark || activeGradingRecord.notes) && (
                <div className="mt-2 p-2.5 bg-amber-100/70 rounded-xl border border-amber-300 text-xs text-amber-950 space-y-1">
                  {activeGradingRecord.rating && (
                    <div className="flex items-center gap-1 font-extrabold text-amber-900 flex-wrap">
                      <span>Tahap Penguasaan Praktikal:</span>
                      <div className="flex items-center gap-0.5 ml-1">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star
                            key={s}
                            className={`w-3.5 h-3.5 ${s <= activeGradingRecord.rating! ? 'fill-amber-500 text-amber-500' : 'text-amber-300'}`}
                          />
                        ))}
                      </div>
                      <span className="ml-1 text-[11px] font-bold">({activeGradingRecord.rating}/5 - {RATING_LEVELS.find(r => r.value === activeGradingRecord.rating)?.label.split(':')[1]?.trim()})</span>
                    </div>
                  )}
                  {(activeGradingRecord.remark || activeGradingRecord.notes) && (
                    <div className="text-[11px] text-amber-900 leading-relaxed">
                      <strong>Remark Ketua Gelanggang:</strong> "{activeGradingRecord.remark || activeGradingRecord.notes}"
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="shrink-0 flex items-center gap-2 w-full sm:w-auto">
            <button
              type="button"
              disabled
              className="w-full sm:w-auto px-4 py-2.5 bg-slate-200 text-slate-500 font-bold text-xs rounded-xl flex items-center justify-center gap-1.5 cursor-not-allowed border border-slate-300"
              title="Menunggu pengesahan gred lulus daripada Ketua Gelanggang"
            >
              <Lock className="w-3.5 h-3.5" />
              <span>Pendaftaran Terkunci (Perlu Gred Lulus)</span>
            </button>
          </div>
        </div>
      )}

      {/* STATE 2: Pelajaran 100% & Gred Lulus Disahkan (Layak Mendaftar / Pra-Daftar) */}
      {(!isManagement || !showManagementPanel) && isEligibleForUjian && (
        <div className="p-3.5 sm:p-4 rounded-2xl border transition-all duration-300 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 text-xs bg-gradient-to-r from-emerald-50 via-teal-50/60 to-emerald-50 border-emerald-300 animate-fadeIn">
          <div className="flex items-start sm:items-center gap-3 text-slate-800 font-medium min-w-0 flex-1">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 border bg-emerald-500 text-white border-emerald-400 shadow-xs ring-2 ring-emerald-100">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                <span className="font-extrabold text-slate-900 text-xs sm:text-sm">
                  Kelayakan Ujian Bengkung — Disahkan Lulus!
                </span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-black border uppercase tracking-wider bg-emerald-100 text-emerald-800 border-emerald-300">
                  Gred Lulus Sah
                </span>
              </div>
              <p className="text-[11px] text-slate-600 leading-relaxed">
                Tahniah <strong>{activeMember ? formatPublicName(activeMember.name) : 'pesilat'}</strong>! Anda telah melengkapkan <strong>{requiredTotalCount} pelajaran</strong> dan disahkan <strong>LULUS</strong> oleh <strong>{activeGradingRecord?.assessorRole} ({activeGradingRecord ? formatPublicName(activeGradingRecord.assessedBy) : ketuaGelanggangName})</strong>. Anda kini layak mendaftar Ujian Bengkung seterusnya (<strong>{targetNextBengkung}</strong>).
              </p>

              {/* Assessment Feedback from Ketua Gelanggang */}
              {activeGradingRecord && (
                <div className="mt-2 p-2.5 bg-emerald-100/70 rounded-xl border border-emerald-300 text-xs text-emerald-950 space-y-1">
                  {activeGradingRecord.rating && (
                    <div className="flex items-center gap-1 font-extrabold text-emerald-900 flex-wrap">
                      <span>Tahap Penguasaan:</span>
                      <div className="flex items-center gap-0.5 ml-1">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star
                            key={s}
                            className={`w-3.5 h-3.5 ${s <= activeGradingRecord.rating! ? 'fill-amber-500 text-amber-500' : 'text-emerald-300'}`}
                          />
                        ))}
                      </div>
                      <span className="ml-1 text-[11px] font-bold">({activeGradingRecord.rating}/5 - {RATING_LEVELS.find(r => r.value === activeGradingRecord.rating)?.label.split(':')[1]?.trim()})</span>
                    </div>
                  )}
                  {(activeGradingRecord.remark || activeGradingRecord.notes) && (
                    <div className="text-[11px] text-emerald-900 leading-relaxed">
                      <strong>Remark Ketua Gelanggang:</strong> "{activeGradingRecord.remark || activeGradingRecord.notes}"
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="shrink-0 flex items-center gap-2 w-full sm:w-auto">
            {activeRegistration?.status === 'pra-daftar' ? (
              <div className="w-full sm:w-auto px-4 py-2.5 bg-emerald-100/90 text-emerald-900 border border-emerald-300 font-extrabold text-xs rounded-xl flex items-center justify-center gap-2 shadow-2xs">
                <Check className="w-4 h-4 text-emerald-700" />
                <span>Pra-Pendaftaran Berjaya (Menunggu Tarikh)</span>
              </div>
            ) : activeRegistration?.status === 'selesai-daftar' ? (
              <div className="w-full sm:w-auto px-4 py-2.5 bg-emerald-700 text-white font-extrabold text-xs rounded-xl flex items-center justify-center gap-2 shadow-xs">
                <Award className="w-4 h-4 text-amber-300" />
                <span>Pendaftaran Ujian Selesai</span>
              </div>
            ) : !activeEvent ? (
              <button
                type="button"
                onClick={() => setIsPraDaftarModalOpen(true)}
                className="w-full sm:w-auto px-4 py-2.5 bg-slate-900 hover:bg-slate-800 active:scale-95 text-white font-extrabold text-xs rounded-xl transition-all flex items-center justify-center gap-1.5 shadow-md hover:shadow-lg cursor-pointer border border-slate-700"
              >
                <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                <span>Pra-Daftar Ujian Bengkung (Percuma)</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  window.history.pushState(null, '', '/payments');
                  window.dispatchEvent(new PopStateEvent('popstate'));
                  showToast(
                    'success',
                    'Pendaftaran Ujian Dibuka',
                    `Tahniah! Membuka portal pendaftaran & pembayaran Ujian Bengkung untuk ${formatPublicName(activeMember?.name || '')}.`
                  );
                }}
                className="w-full sm:w-auto px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white font-extrabold text-xs rounded-xl transition-all flex items-center justify-center gap-1.5 shadow-md hover:shadow-lg cursor-pointer border border-emerald-500"
              >
                <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                <span>Daftar & Bayar Yuran Ujian</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Management Syllabus Manager Panel */}
      {isManagement && showManagementPanel && (
        <DashboardSyllabusManager />
      )}

      {/* MOBILE VIEW SEGMENTED TABS (Hidden on md/tablet & desktop screens, visible only on phones) */}
      <div className="md:hidden flex items-center p-1 bg-slate-100/90 rounded-2xl border border-slate-200">
        <button
          type="button"
          onClick={() => setMobileWorkspaceTab('curriculum')}
          className={`flex-1 py-2.5 text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer ${mobileWorkspaceTab === 'curriculum'
            ? 'bg-white text-slate-900 shadow-sm'
            : 'text-slate-500 hover:text-slate-700'
            }`}
        >
          <BookOpen className="w-3.5 h-3.5 text-asmm-red" />
          <span>Kandungan ({allLessons.length})</span>
        </button>
        <button
          type="button"
          onClick={() => setMobileWorkspaceTab('content')}
          className={`flex-1 py-2.5 text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer ${mobileWorkspaceTab === 'content'
            ? 'bg-white text-slate-900 shadow-sm'
            : 'text-slate-500 hover:text-slate-700'
            }`}
        >
          <FileText className="w-3.5 h-3.5 text-amber-600" />
          <span>Info Pelajaran</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-5 lg:gap-6 items-start">

        {/* LEFT COLUMN: Main Player & Active Content Workspace (7 Cols on md/tablet & desktop) */}
        <div id="syllabus-player-workspace" className={`${mobileWorkspaceTab === 'content' ? 'block' : 'hidden md:block'} md:col-span-7 xl:col-span-7 space-y-4`}>
          <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
            {/* Player Container */}
            <div className="relative bg-[#1C1D1F] aspect-video flex items-center justify-center text-white p-4">
              {!isActiveLessonUnlocked ? (
                <div className="text-center space-y-3 p-4 sm:p-6 max-w-md mx-auto">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center mx-auto border border-amber-400/30">
                    <Lock className="w-6 h-6 sm:w-8 sm:h-8" />
                  </div>
                  <div className="space-y-1">
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-amber-400 bg-amber-950/80 border border-amber-500/40 px-2.5 py-0.5 rounded-full">
                      Akses Modul Terhad
                    </span>
                    <div className="text-xs sm:text-sm font-bold text-slate-100 line-clamp-2 pt-1">
                      {activeLesson.title}
                    </div>
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed font-medium">
                    Kandungan video dan dokumen bagi modul ini dikunci untuk tahap bengkung semasa anda ({activeMember?.bengkung || 'Bengkung Semasa'}). Sila selesaikan kurikulum dan lulus Ujian Bengkung {activeLessonSection?.requiredBengkungName} untuk membuka akses.
                  </p>
                  <div className="pt-1 flex items-center justify-center gap-2">
                    <span className="text-[10px] sm:text-[11px] text-amber-300 bg-slate-800/90 border border-slate-700 px-3 py-1 rounded-full font-bold inline-flex items-center gap-1.5">
                      <ShieldCheck className="w-3.5 h-3.5 text-amber-400" />
                      Memerlukan {activeLessonSection?.requiredBengkungName}
                    </span>
                  </div>
                </div>
              ) : activeLesson.type === 'video' ? (
                isPlayingVideo ? (
                  <div className="w-full h-full relative flex items-center justify-center bg-black">
                    <video
                      controls
                      autoPlay
                      className="w-full h-full object-contain bg-black"
                      src={activeLesson.videoUrl || 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4'}
                    >
                      Pelayar anda tidak menyokong elemen video.
                    </video>
                  </div>
                ) : (
                  <div className="text-center space-y-2.5 p-4 sm:p-6 max-w-md mx-auto">
                    <div
                      className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-asmm-red/90 hover:bg-asmm-crimson text-white flex items-center justify-center mx-auto shadow-lg cursor-pointer hover:scale-105 active:scale-95 transition-all"
                      onClick={() => {
                        setIsPlayingVideo(true);
                        showToast('info', 'Memainkan Video Latihan...', activeLesson.title);
                      }}
                    >
                      <Play className="w-6 h-6 sm:w-8 sm:h-8 fill-current ml-1" />
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-xs text-amber-300 bg-slate-800/90 px-3.5 py-1.5 rounded-full border border-slate-700 font-semibold shadow-xs">
                      <Clock className="w-3.5 h-3.5 text-amber-400" />
                      Video Pelajaran ({activeLesson.duration}) • Klik Untuk Main
                    </span>
                  </div>
                )
              ) : activeLesson.type === 'pdf' ? (
                <div className="text-center space-y-3.5 p-6">
                  <FileText className="w-12 h-12 text-amber-400 mx-auto" />
                  <div className="text-xs font-semibold text-slate-300">Fail Dokumen Rasmi Silibus ({activeLesson.duration || 'PDF'})</div>
                  <button
                    onClick={() => showToast('success', `Memuat Turun Fail PDF: ${activeLesson.title}`)}
                    className="px-4 py-2 bg-asmm-red hover:bg-asmm-crimson text-white text-xs font-bold rounded-xl shadow-md inline-flex items-center gap-1.5 cursor-pointer active:scale-95 transition-all"
                  >
                    <Download className="w-4 h-4" />
                    Muat Turun Fail PDF
                  </button>
                </div>
              ) : (
                <div className="text-center space-y-3.5 p-6">
                  <BookOpen className="w-12 h-12 text-emerald-400 mx-auto" />
                  <span className="inline-block text-xs text-emerald-300 bg-slate-800/90 px-3.5 py-1.5 rounded-full border border-slate-700 font-semibold shadow-xs">
                    Modul Bacaan & Panduan Adab ({activeLesson.duration || 'Bacaan'})
                  </span>
                </div>
              )}
            </div>

            {/* Active Lesson Meta & Navigation Header */}
            <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
              <div className="space-y-3 border-b border-slate-100 pb-3 sm:pb-4">
                {/* Badges */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[9px] sm:text-[10px] font-extrabold uppercase tracking-wider text-asmm-red bg-red-50 px-2 py-0.5 rounded border border-red-100">
                    Pelajaran Semasa
                  </span>
                  {activeMember && (
                    <span className="text-[9px] sm:text-[10px] font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded truncate max-w-[220px]">
                      Pelajar: {formatPublicName(activeMember.name)}
                    </span>
                  )}
                </div>

                {/* Lesson Title - 100% full width to prevent squished wrapping */}
                <h3 className="text-base sm:text-lg lg:text-xl font-extrabold text-slate-900 leading-snug w-full">
                  {activeLesson.title}
                </h3>

                {/* Meta details & Action Buttons Row */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-1">
                  <div className="flex items-center gap-3 text-[11px] text-slate-500 font-medium">
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-slate-400" /> {activeLesson.duration}</span>
                    <span>•</span>
                    <span className="uppercase font-bold text-slate-700">{activeLesson.type}</span>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap shrink-0">
                    <button
                      type="button"
                      disabled={!isActiveLessonUnlocked}
                      onClick={() => toggleLessonComplete(activeLesson.id, activeLessonSection?.bengkungRank)}
                      className={`px-3.5 sm:px-4 py-2 rounded-xl text-xs font-bold border transition-all flex items-center justify-center gap-2 shrink-0 shadow-xs ${!isActiveLessonUnlocked
                        ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                        : isCurrentLessonCompleted
                          ? 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-500 shadow-emerald-200 ring-2 ring-emerald-100 cursor-pointer active:scale-95'
                          : 'bg-white hover:bg-slate-50 text-slate-700 border-slate-300 hover:border-slate-400 cursor-pointer active:scale-95'
                        }`}
                    >
                      {!isActiveLessonUnlocked ? (
                        <>
                          <Lock className="w-3.5 h-3.5 text-amber-600" />
                          <span>Modul Terkunci</span>
                        </>
                      ) : isCurrentLessonCompleted ? (
                        <>
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-100" />
                          <span>{activeLessonSection && isPreviousBengkungRank(activeMember?.bengkung, activeLessonSection.bengkungRank) ? 'Selesai' : 'Selesai'}</span>
                        </>
                      ) : (
                        <>
                          <Square className="w-3.5 h-3.5 text-slate-400" />
                          <span>Tanda Selesai</span>
                        </>
                      )}
                    </button>

                    {canEvaluateActiveMember && activeMember && (
                      <button
                        type="button"
                        onClick={() => handleOpenLessonEvaluation(activeLesson)}
                        className="px-3.5 sm:px-4 py-2 rounded-xl text-xs font-bold border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 transition-all flex items-center justify-center gap-1.5 shrink-0 shadow-2xs cursor-pointer active:scale-95"
                        title={`Nilai & Remark: ${activeLesson.title}`}
                      >
                        <Star className={`w-3.5 h-3.5 ${activeGradingRecord?.lessonRatings?.[activeLesson.id] ? 'fill-amber-500 text-amber-500' : 'text-amber-600'}`} />
                        <span>
                          {activeGradingRecord?.lessonRatings?.[activeLesson.id]
                            ? `${activeGradingRecord.lessonRatings[activeLesson.id].rating}/5 • Edit Remark`
                            : 'Nilai & Remark Tajuk'}
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Prev / Next Quick Jump Bar */}
              <div className="flex items-center justify-between gap-2 pt-0.5 border-b border-slate-100 pb-3 text-xs">
                <button
                  type="button"
                  disabled={!prevLesson}
                  onClick={() => {
                    if (prevLesson) {
                      setIsPlayingVideo(false);
                      setActiveLesson(prevLesson);
                    }
                  }}
                  className={`px-3 py-1.5 rounded-xl border font-bold flex items-center gap-1 transition-all ${prevLesson
                    ? 'text-slate-700 bg-slate-50 hover:bg-slate-100 border-slate-200 cursor-pointer active:scale-95'
                    : 'text-slate-300 bg-slate-50 border-slate-100 cursor-not-allowed'
                    }`}
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  <span>Sebelumnya</span>
                </button>

                <span className="text-[10px] sm:text-[11px] font-extrabold text-slate-500 font-mono">
                  {currentLessonIndex >= 0 ? `${currentLessonIndex + 1} / ${accessibleLessons.length}` : ''}
                </span>

                <button
                  type="button"
                  disabled={!nextLesson}
                  onClick={() => {
                    if (nextLesson) {
                      setIsPlayingVideo(false);
                      setActiveLesson(nextLesson);
                    }
                  }}
                  className={`px-3 py-1.5 rounded-xl border font-bold flex items-center gap-1 transition-all ${nextLesson
                    ? 'text-white bg-asmm-red hover:bg-asmm-crimson border-asmm-red cursor-pointer active:scale-95 shadow-xs'
                    : 'text-slate-300 bg-slate-50 border-slate-100 cursor-not-allowed'
                    }`}
                >
                  <span>Seterusnya</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Detailed Description & Reference Materials (Visible on desktop & tablet OR when mobileWorkspaceTab === 'content') */}
              <div className={`${mobileWorkspaceTab === 'content' ? 'block' : 'hidden md:block'} space-y-4 pt-1 animate-fadeIn`}>
                <div className="space-y-1.5">
                  <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Kandungan & Penerangan Pelajaran:</h4>
                  <p className="text-xs text-slate-600 leading-relaxed font-medium">
                    {activeLesson.description}
                  </p>
                </div>

                {/* Penilaian Penguasaan Pelajaran Ini (Paparan Pelajar Sahaja, Bukan Borang Penilaian Ketua Gelanggang) */}
                {!canEvaluateActiveMember && activeMember && activeGradingRecord?.lessonRatings && activeGradingRecord.lessonRatings[activeLesson.id] && (
                  <div className="p-3.5 sm:p-4 bg-amber-50/40 rounded-2xl border border-amber-200/80 space-y-2.5">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="font-extrabold text-xs text-slate-900 flex items-center gap-1.5">
                        <Award className="w-4 h-4 text-amber-500 shrink-0" />
                        Penilaian &amp; Ulasan Pelajaran Ini
                      </span>
                    </div>

                    <div className="space-y-2 pt-1">
                      <div className="flex items-center justify-between gap-2 flex-wrap bg-white/95 p-2.5 rounded-xl border border-amber-200">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-amber-950 font-extrabold">Tahap Penguasaan:</span>
                          <div className="flex items-center gap-0.5 ml-1">
                            {[1, 2, 3, 4, 5].map((s) => (
                              <Star
                                key={s}
                                className={`w-3.5 h-3.5 ${s <= (activeGradingRecord.lessonRatings![activeLesson.id]?.rating || 0) ? 'fill-amber-500 text-amber-500' : 'text-slate-300'}`}
                              />
                            ))}
                          </div>
                          <span className="text-xs font-black text-amber-900 font-mono">
                            ({activeGradingRecord.lessonRatings![activeLesson.id]?.rating}/5)
                          </span>
                        </div>
                        <span className={`text-[10px] font-black px-2.5 py-0.5 rounded-full border ${RATING_LEVELS.find(r => r.value === activeGradingRecord.lessonRatings![activeLesson.id]?.rating)?.badgeColor
                          }`}>
                          {RATING_LEVELS.find(r => r.value === activeGradingRecord.lessonRatings![activeLesson.id]?.rating)?.label}
                        </span>
                      </div>

                      {activeGradingRecord.lessonRatings![activeLesson.id]?.remark ? (
                        <div className="p-3 bg-white rounded-xl border border-amber-300 shadow-2xs space-y-1.5">
                          <div className="flex items-center justify-between text-[10px] text-amber-800 font-extrabold">
                            <span className="flex items-center gap-1">
                              <Lightbulb className="w-3.5 h-3.5 text-amber-600" />
                              Catatan Penambahbaikan Daripada Jurulatih:
                            </span>
                            {activeGradingRecord.lessonRatings![activeLesson.id]?.assessedBy && (
                              <span className="text-slate-400 font-normal">
                                Oleh {activeGradingRecord.lessonRatings![activeLesson.id]?.assessedBy}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-800 italic font-semibold leading-relaxed bg-amber-50/60 p-2 rounded-lg border border-amber-200/80">
                            "{activeGradingRecord.lessonRatings![activeLesson.id]?.remark}"
                          </p>
                          <p className="text-[10px] text-slate-500 font-medium">
                            💡 Sila teliti video dan langkah di atas, dan perbaiki bahagian ini semasa sesi latihan di gelanggang.
                          </p>
                        </div>
                      ) : (
                        <p className="text-[11px] text-slate-500 italic bg-white/80 p-2 rounded-xl border border-slate-200">
                          Jurulatih telah menilai pelajaran ini pada tahap penguasaan {activeGradingRecord.lessonRatings![activeLesson.id]?.rating}/5 bintang.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: Curriculum Accordion (Visible on desktop & tablet OR when mobileWorkspaceTab === 'curriculum') */}
        <div className={`${mobileWorkspaceTab === 'curriculum' ? 'block' : 'hidden md:block'} md:col-span-5 xl:col-span-5 space-y-3 md:sticky md:top-20 md:max-h-[calc(100vh-100px)] md:overflow-y-auto pr-1 scrollbar-thin animate-fadeIn`}>
          <div className="flex items-center justify-between px-1">
            <h3 className="text-xs sm:text-sm font-bold text-slate-900 uppercase tracking-wider">
              Kandungan Kursus ({visibleSections.length} Bahagian)
            </h3>
            <span className="text-xs font-bold text-asmm-red">{allLessons.length} Pelajaran</span>
          </div>

          {/* Curriculum Sections Accordion */}
          <div className="space-y-2.5 sm:space-y-3">
            {visibleSections.map((section, sIdx) => {
              const prevSection = sIdx > 0 ? visibleSections[sIdx - 1] : null;
              const isNewCategory = Boolean(section.category && (!prevSection || prevSection.category !== section.category));
              const unlocked = isRankUnlocked(section.bengkungRank);
              const isOpen = openSectionIds.includes(section.id);
              const secCompleted = section.lessons.filter(l => l.completed).length;

              return (
                <React.Fragment key={section.id}>
                  {isNewCategory && (
                    <div className="pt-3 pb-1 first:pt-0">
                      <div className="px-3.5 py-2 bg-slate-900 text-white rounded-xl flex items-center justify-between shadow-xs">
                        <span className="text-[11px] sm:text-xs font-black tracking-wider uppercase flex items-center gap-1.5">
                          <Award className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                          {section.category}
                        </span>
                      </div>
                    </div>
                  )}

                  <div
                    className={`bg-white rounded-2xl border transition-all shadow-sm overflow-hidden ${unlocked ? 'border-slate-200' : 'border-amber-200 bg-amber-50/20'
                      }`}
                  >
                    {/* Section Accordion Header */}
                    <button
                      onClick={() => toggleSectionOpen(section.id)}
                      className={`w-full p-3.5 sm:p-4 flex items-center justify-between text-left transition-colors cursor-pointer ${unlocked ? 'bg-slate-50 hover:bg-slate-100/80' : 'bg-amber-50/40 hover:bg-amber-100/50'
                        }`}
                    >
                      <div className="flex items-center gap-2.5 sm:gap-3 overflow-hidden">
                        <img src={section.badge} alt="Badge" className="w-7 h-7 sm:w-8 sm:h-8 object-contain shrink-0" />
                        <div className="overflow-hidden">
                          <div className="font-bold text-xs text-slate-900 flex items-center gap-1.5 truncate">
                            {!unlocked && <Lock className="w-3.5 h-3.5 text-amber-600 shrink-0" />}
                            <span className="truncate">{section.title}</span>
                          </div>
                          <div className="text-[10px] text-slate-500 font-medium mt-0.5 flex items-center gap-2">
                            {section.rankSubtitle && !section.title.toLowerCase().includes(section.rankSubtitle.toLowerCase()) && (
                              <span className="font-bold text-amber-800 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200/80">
                                {section.rankSubtitle}
                              </span>
                            )}
                            <span className="flex items-center gap-1.5 flex-wrap">
                              <span>{unlocked ? `${secCompleted} / ${section.lessons.length} Selesai` : `Memerlukan ${section.requiredBengkungName}`}</span>
                              {unlocked && isPreviousBengkungRank(activeMember?.bengkung, section.bengkungRank) && (
                                <span className="inline-flex items-center gap-1 text-[9px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200/80 px-1.5 py-0.5 rounded-full">
                                  <CheckCircle2 className="w-2.5 h-2.5 text-emerald-600" />
                                  Selesai
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        {!unlocked && (
                          <span className="text-[9px] sm:text-[10px] font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded border border-amber-300 flex items-center gap-1">
                            <Lock className="w-3 h-3 text-amber-600" />
                            Terkunci
                          </span>
                        )}
                        {isOpen ? (
                          <ChevronUp className="w-4 h-4 text-slate-500" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-slate-500" />
                        )}
                      </div>
                    </button>

                    {/* Accordion Lesson List (Always expandable to view curriculum outline & titles) */}
                    {isOpen && (
                      <div className="divide-y divide-slate-100 bg-white">
                        {!unlocked && (
                          <div className="px-3.5 py-2.5 bg-amber-50/70 border-b border-amber-200/60 flex items-center gap-2 text-amber-900">
                            <Lock className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                            <span className="text-[11px] font-medium leading-tight">
                              Modul ini memerlukan kelulusan <strong>{section.requiredBengkungName}</strong>. Tajuk pelajaran dipaparkan bagi tujuan semakan kurikulum, namun kandungan video &amp; dokumen dikunci.
                            </span>
                          </div>
                        )}
                        {section.lessons.map((lesson, lIdx) => {
                          const isActive = activeLesson.id === lesson.id;
                          const lessonEvaluation = activeGradingRecord?.lessonRatings?.[lesson.id];

                          return (
                            <div
                              key={lesson.id}
                              id={`syllabus-lesson-${lesson.id}`}
                              className={`p-3.5 sm:p-4 flex flex-col gap-2.5 sm:gap-3 transition-all ${isActive
                                ? 'bg-red-50/50 border-l-4 border-asmm-red shadow-2xs'
                                : unlocked
                                  ? 'hover:bg-slate-50/80 bg-white'
                                  : 'bg-slate-50/40 hover:bg-amber-50/20'
                                }`}
                            >
                              {/* Top Row: Checkbox, Full Title & Meta, Quick Play / Lock */}
                              <div className="flex items-start gap-2.5 sm:gap-3">
                                {/* Checkbox toggle with generous touch target */}
                                <button
                                  type="button"
                                  disabled={!unlocked}
                                  onClick={() => {
                                    if (!unlocked) {
                                      showToast('warning', 'Modul Terkunci', `Pelajaran ini terkunci. Sila lulus ${section.requiredBengkungName} terlebih dahulu.`);
                                      return;
                                    }
                                    toggleLessonComplete(lesson.id, section.bengkungRank);
                                  }}
                                  className={`mt-0.5 p-1 -m-1 transition-colors shrink-0 ${unlocked ? 'text-slate-400 hover:text-emerald-600 cursor-pointer' : 'text-slate-300 cursor-not-allowed opacity-70'
                                    }`}
                                  title={!unlocked ? 'Modul Terkunci' : isPreviousBengkungRank(activeMember?.bengkung, section.bengkungRank) ? 'Tahap bengkung ini telah disahkan selesai mengikut tahap bengkung ahli' : lesson.completed ? 'Tanda belum selesai' : 'Tanda selesai'}
                                >
                                  {lesson.completed ? (
                                    <CheckSquare className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-emerald-600" />
                                  ) : !unlocked ? (
                                    <Lock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-600/70" />
                                  ) : (
                                    <Square className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-slate-300 hover:text-slate-400" />
                                  )}
                                </button>

                                {/* Title & Metadata (Takes maximum available width) */}
                                <div
                                  onClick={() => handleSelectLesson(lesson, section.bengkungRank, section.requiredBengkungName)}
                                  className="flex-1 min-w-0 cursor-pointer group"
                                >
                                  <div className={`text-xs sm:text-sm font-bold leading-snug transition-colors line-clamp-2 ${isActive ? 'text-asmm-red' : unlocked ? 'text-slate-900 group-hover:text-asmm-red' : 'text-slate-600'
                                    }`}>
                                    <span className="font-mono text-slate-400 mr-1.5 text-xs font-semibold">{lIdx + 1}.</span>
                                    {lesson.title.replace(/^\d+\.\s*/, '')}
                                  </div>
                                  <div className="flex items-center gap-2 text-[10px] sm:text-[11px] text-slate-500 mt-1 font-medium flex-wrap">
                                    {lesson.type === 'video' && (
                                      <span className="inline-flex items-center gap-1 text-slate-600 font-mono">
                                        <Video className="w-3 h-3 text-slate-400" />
                                        {lesson.duration}
                                      </span>
                                    )}
                                    {lesson.type === 'pdf' && (
                                      <span className="inline-flex items-center gap-1 text-amber-700 font-mono">
                                        <FileText className="w-3 h-3 text-amber-600" />
                                        {lesson.duration || 'PDF'}
                                      </span>
                                    )}
                                    {lesson.type === 'reading' && (
                                      <span className="inline-flex items-center gap-1 text-emerald-700 font-mono">
                                        <BookOpen className="w-3 h-3 text-emerald-600" />
                                        {lesson.duration || 'Bacaan'}
                                      </span>
                                    )}
                                    {isActive && (
                                      <span className="text-[9px] sm:text-[10px] font-bold text-asmm-red bg-red-100/90 px-1.5 py-0.5 rounded-md">
                                        Pelajaran Semasa
                                      </span>
                                    )}
                                    {!unlocked && (
                                      <span className="text-[9px] sm:text-[10px] text-amber-700 bg-amber-100/90 px-1.5 py-0.5 rounded-md font-semibold inline-flex items-center gap-0.5">
                                        <Lock className="w-2.5 h-2.5" /> Terkunci
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Play / Lock Action Button */}
                                {unlocked ? (
                                  <button
                                    type="button"
                                    onClick={() => handleSelectLesson(lesson, section.bengkungRank, section.requiredBengkungName)}
                                    className={`p-2 sm:p-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer shrink-0 ${isActive
                                      ? 'bg-asmm-red text-white shadow-xs'
                                      : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100 bg-slate-50'
                                      }`}
                                    title="Mainkan Pelajaran"
                                  >
                                    <Play className="w-3.5 h-3.5 fill-current" />
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => handleSelectLesson(lesson, section.bengkungRank, section.requiredBengkungName)}
                                    className="p-2 sm:p-2.5 rounded-xl text-xs text-amber-600 bg-amber-50 hover:bg-amber-100/80 transition-all cursor-pointer shrink-0"
                                    title={`Modul Terkunci (${section.requiredBengkungName})`}
                                  >
                                    <Lock className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>

                              {/* Middle Row: Full-width Jurulatih Remark & Rating Card */}
                              {lessonEvaluation && (
                                <div
                                  onClick={(e) => {
                                    if (canEditLessonEvaluation && activeMember) {
                                      e.stopPropagation();
                                      handleOpenLessonEvaluation(lesson);
                                    }
                                  }}
                                  className={`w-full p-2.5 sm:p-3 bg-gradient-to-r from-amber-50/90 via-amber-50/50 to-orange-50/40 rounded-xl border border-amber-200/90 text-amber-950 transition-all shadow-2xs ${canEditLessonEvaluation && activeMember ? 'cursor-pointer hover:bg-amber-100/70 hover:border-amber-300' : ''
                                    }`}
                                  title={canEditLessonEvaluation ? `Klik untuk kemaskini penilaian: ${lesson.title}` : undefined}
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <span className="text-[11px] font-bold text-amber-950">Penguasaan:</span>
                                      <div className="flex items-center gap-0.5">
                                        {[1, 2, 3, 4, 5].map((s) => (
                                          <Star
                                            key={s}
                                            className={`w-3 h-3 ${s <= (lessonEvaluation.rating || 0)
                                              ? 'fill-amber-500 text-amber-500'
                                              : 'text-slate-300'
                                              }`}
                                          />
                                        ))}
                                      </div>
                                      <span className="text-[11px] font-extrabold text-amber-900">
                                        ({lessonEvaluation.rating}/5)
                                      </span>
                                    </div>

                                    {canEditLessonEvaluation && activeMember && (
                                      <span className="text-[10px] font-bold text-amber-800 bg-white/90 px-2 py-0.5 rounded-lg border border-amber-300 flex items-center gap-1 shrink-0 shadow-2xs hover:bg-amber-100/80">
                                        <Edit3 className="w-2.5 h-2.5 text-amber-700" />
                                        Edit Ulasan
                                      </span>
                                    )}
                                  </div>

                                  {lessonEvaluation.remark && (
                                    <div className="mt-2 text-xs text-slate-800 leading-relaxed font-medium bg-white/95 p-2.5 rounded-lg border border-amber-200/70 shadow-2xs">
                                      <div className="flex items-start gap-1.5">
                                        <Lightbulb className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                                        <div className="flex-1 min-w-0">
                                          <span className="font-bold text-amber-900 mr-1 text-[11px] uppercase tracking-wide">
                                            Ulasan Jurulatih:
                                          </span>
                                          <span className="italic text-slate-700 break-words">
                                            "{lessonEvaluation.remark}"
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {lessonEvaluation.assessedBy && (
                                    <div className="text-[10px] text-amber-800/80 font-medium pt-1 flex items-center justify-between flex-wrap gap-1">
                                      <span>Oleh: {lessonEvaluation.assessedBy} ({activeGradingRecord?.assessorRole || 'Ketua Gelanggang'})</span>
                                      {isUserSuperAdmin && (
                                        <span className="text-[9px] text-amber-700 font-semibold bg-amber-100/80 px-1.5 py-0.2 rounded border border-amber-200">
                                          Superadmin: Klik untuk kemaskini ulasan
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Master Syllabus Remark (if any and not evaluated yet) */}
                              {lesson.remark && !lessonEvaluation && (
                                <div className="w-full text-[11px] text-slate-600 italic flex items-center gap-1.5 bg-slate-50 p-2 rounded-lg border border-slate-200/60">
                                  <HelpCircle className="w-3 h-3 text-slate-400 shrink-0" />
                                  <span className="truncate">{lesson.remark}</span>
                                </div>
                              )}

                              {/* Bottom Action Row: Jurulatih Evaluation CTA & Management Tools */}
                              {(canCreateLessonEvaluation && activeMember && !lessonEvaluation) || isManagement ? (
                                <div className="flex items-center justify-between gap-2 pt-1 border-t border-slate-100/80">
                                  {canCreateLessonEvaluation && activeMember && !lessonEvaluation ? (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenLessonEvaluation(lesson);
                                      }}
                                      className="py-1.5 px-3 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 bg-amber-50 hover:bg-amber-100 text-amber-900 border border-amber-200 hover:border-amber-300 shadow-2xs active:scale-98"
                                      title={`Nilai & Remark: ${lesson.title}`}
                                    >
                                      <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-600" />
                                      <span>Nilai & Remark Tajuk</span>
                                    </button>
                                  ) : (
                                    <div />
                                  )}

                                  {isManagement && (
                                    <div className="flex items-center gap-1 ml-auto shrink-0">
                                      <button
                                        type="button"
                                        onClick={() => handleOpenEditLesson(lesson, section.id)}
                                        className="p-1.5 px-2 rounded-lg text-xs font-medium text-slate-600 hover:text-blue-700 hover:bg-blue-50 border border-transparent hover:border-blue-200 transition-all cursor-pointer flex items-center gap-1"
                                        title="Kemaskini Pelajaran"
                                      >
                                        <Edit3 className="w-3.5 h-3.5" />
                                        <span className="text-[10px] hidden sm:inline">Edit</span>
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteLesson(lesson.id, lesson.title)}
                                        className="p-1.5 px-2 rounded-lg text-xs font-medium text-slate-600 hover:text-rose-700 hover:bg-rose-50 border border-transparent hover:border-rose-200 transition-all cursor-pointer flex items-center gap-1"
                                        title="Padam Pelajaran"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                        <span className="text-[10px] hidden sm:inline">Padam</span>
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* LESSON CREATION MODAL */}
      {isAddLessonModalOpen && (
        <Modal
          isOpen={isAddLessonModalOpen}
          onClose={() => setIsAddLessonModalOpen(false)}
          title="Tambah Pelajaran Baru Kurikulum"
        >
          <form onSubmit={handleAddLesson} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Tajuk Pelajaran *</label>
              <input
                type="text"
                required
                value={newLessonTitle}
                onChange={(e) => setNewLessonTitle(e.target.value)}
                placeholder="Contoh: Jurus 6 & 7 Awang Sulung"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Peringkat Modul (Bengkung)</label>
                <select
                  value={newLessonTargetRank}
                  onChange={(e) => setNewLessonTargetRank(e.target.value as any)}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none text-xs text-slate-900"
                >
                  <option value="hitam">Bengkung Hitam (Tunas Pendekar)</option>
                  <option value="putih">Bengkung Putih (Pesilat)</option>
                  <option value="merah">Bengkung Merah (Pembantu Jurulatih)</option>
                  <option value="kuning">Bengkung Kuning (Jurulatih)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Jenis Kandungan</label>
                <select
                  value={newLessonType}
                  onChange={(e) => setNewLessonType(e.target.value as any)}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none text-xs text-slate-900"
                >
                  <option value="video">Video Latihan</option>
                  <option value="pdf">Fail Dokumen PDF</option>
                  <option value="reading">Manual Bacaan</option>
                </select>
              </div>
            </div>

            {newLessonType !== 'pdf' && (
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Durasi / Masa Pelajaran</label>
                <input
                  type="text"
                  value={newLessonDuration}
                  onChange={(e) => setNewLessonDuration(e.target.value)}
                  placeholder="Contoh: 15:30 atau 05:00"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-mono"
                />
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Penerangan / Falsafah Pelajaran *</label>
              <textarea
                required
                rows={3}
                value={newLessonDescription}
                onChange={(e) => setNewLessonDescription(e.target.value)}
                placeholder="Penerangan terperinci sukatan pelajaran atau buah kuncian..."
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900"
              />
            </div>

            <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setIsAddLessonModalOpen(false)}
                className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 cursor-pointer"
              >
                Batal
              </button>
              <button
                type="submit"
                className="px-5 py-2 bg-asmm-red text-white font-bold text-xs rounded-xl hover:bg-asmm-crimson transition-all flex items-center gap-1.5 cursor-pointer"
              >
                <CheckSquare className="w-3.5 h-3.5" />
                <span>Simpan Pelajaran</span>
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* DELETE LESSON CONFIRMATION MODAL */}
      {confirmDeleteLessonId && (
        <Modal
          isOpen={!!confirmDeleteLessonId}
          onClose={() => setConfirmDeleteLessonId(null)}
          title="Sahkan Pemadaman Pelajaran"
        >
          <div className="space-y-4">
            <p className="text-sm text-slate-700">
              Adakah anda pasti ingin memadam pelajaran <strong className="text-slate-900">{confirmDeleteLessonId.title}</strong>?
            </p>
            <p className="text-xs text-red-600 font-medium">Tindakan ini tidak boleh dibatalkan.</p>
            <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
              <button
                onClick={() => setConfirmDeleteLessonId(null)}
                className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 cursor-pointer"
              >
                Batal
              </button>
              <button
                onClick={confirmDeleteLesson}
                className="px-5 py-2 bg-red-600 text-white font-bold text-xs rounded-xl hover:bg-red-700 transition-all flex items-center gap-1.5 cursor-pointer"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Padam Pelajaran</span>
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* EDIT LESSON MODAL */}
      {isEditLessonModalOpen && editLessonData && (
        <Modal
          isOpen={isEditLessonModalOpen}
          onClose={() => { setIsEditLessonModalOpen(false); setEditLessonData(null); }}
          title="Kemaskini Pelajaran"
        >
          <form onSubmit={handleSaveEditLesson} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Tajuk Pelajaran *</label>
              <input
                type="text"
                required
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Jenis Kandungan</label>
                <select
                  value={editType}
                  onChange={(e) => setEditType(e.target.value as any)}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none text-xs text-slate-900"
                >
                  <option value="video">Video Latihan</option>
                  <option value="pdf">Fail Dokumen PDF</option>
                  <option value="reading">Manual Bacaan</option>
                </select>
              </div>
              {editType !== 'pdf' && (
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Durasi</label>
                  <input
                    type="text"
                    value={editDuration}
                    onChange={(e) => setEditDuration(e.target.value)}
                    placeholder="15:30"
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-mono"
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Penerangan *</label>
              <textarea
                required
                rows={3}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900"
              />
            </div>

            {editType === 'video' && (
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">URL Video Latihan</label>
                <input
                  type="url"
                  value={editVideoUrl}
                  onChange={(e) => setEditVideoUrl(e.target.value)}
                  placeholder="https://drive.google.com/... atau URL video lain"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-mono"
                />
                <p className="text-[10px] text-slate-400 mt-1">Biarkan kosong untuk video contoh.</p>
              </div>
            )}

            {editType === 'pdf' && (
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">ID Fail Google Drive (PDF)</label>
                <input
                  type="text"
                  value={editPdfFileId}
                  onChange={(e) => setEditPdfFileId(e.target.value)}
                  placeholder="gdrive-doc-03"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-mono"
                />
              </div>
            )}

            <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => { setIsEditLessonModalOpen(false); setEditLessonData(null); }}
                className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 cursor-pointer"
              >
                Batal
              </button>
              <button
                type="submit"
                className="px-5 py-2 bg-asmm-red text-white font-bold text-xs rounded-xl hover:bg-asmm-crimson transition-all flex items-center gap-1.5 cursor-pointer"
              >
                <Edit3 className="w-3.5 h-3.5" />
                <span>Kemaskini Pelajaran</span>
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modal Pra-Pendaftaran Ujian Bengkung Tahunan (Tanpa Bayaran Awal) */}
      {isPraDaftarModalOpen && activeMember && (
        <Modal
          isOpen={isPraDaftarModalOpen}
          onClose={() => setIsPraDaftarModalOpen(false)}
          title="Borang Pra-Pendaftaran Ujian Kenaikan Bengkung"
        >
          <div className="space-y-4">
            <div className="p-3.5 bg-amber-50 border border-amber-200/80 rounded-2xl flex items-start gap-2.5 text-xs text-amber-900">
              <Sparkles className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <span className="font-extrabold block text-amber-950">Pra-Pendaftaran Calon Layak (Tiada Bayaran Awal)</span>
                <span className="text-[11px] leading-relaxed text-amber-800">
                  Ujian Bengkung diadakan secara tahunan pada tarikh yang fleksibel. Pra-pendaftaran ini bertujuan merekodkan nama calon yang telah menamatkan silibus dan disahkan lulus oleh Ketua Gelanggang. Tiada bayaran dikenakan sekarang. Sebaik sahaja pengurusan menetapkan tarikh rasmi, anda akan menerima notifikasi di Dashboard untuk melengkapkan pendaftaran dan pembayaran.
                </span>
              </div>
            </div>

            <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 space-y-2.5 text-xs">
              <div className="flex justify-between items-center py-1 border-b border-slate-200/60">
                <span className="text-slate-500 font-bold">Nama Calon Pesilat:</span>
                <span className="text-slate-900 font-extrabold">{activeMember.name}</span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-slate-200/60">
                <span className="text-slate-500 font-bold">No. Kad Pengenalan (IC):</span>
                <span className="text-slate-900 font-mono font-bold">{activeMember.icNo}</span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-slate-200/60">
                <span className="text-slate-500 font-bold">Gelanggang Latihan:</span>
                <span className="text-slate-900 font-extrabold">{activeMember.gelanggang}</span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-slate-200/60">
                <span className="text-slate-500 font-bold">Bengkung Semasa:</span>
                <span className="text-slate-900 font-extrabold">{activeMember.bengkung || 'Bengkung Putih'}</span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-slate-200/60">
                <span className="text-slate-500 font-bold">Bengkung Sasaran Ujian:</span>
                <span className="text-asmm-red font-black text-sm">{targetNextBengkung}</span>
              </div>
              <div className="flex justify-between items-center py-1">
                <span className="text-slate-500 font-bold">Pengesah Gred Lulus:</span>
                <span className="text-emerald-700 font-extrabold">
                  {activeGradingRecord?.assessedBy} ({activeGradingRecord?.assessorRole})
                </span>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setIsPraDaftarModalOpen(false)}
                className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 cursor-pointer"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={handleConfirmPraDaftar}
                disabled={isSavingPraDaftar}
                className="px-5 py-2.5 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold text-xs rounded-xl transition-all shadow-md flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                <Send className="w-3.5 h-3.5" />
                <span>{isSavingPraDaftar ? 'Merekodkan...' : 'Hantar Pra-Pendaftaran'}</span>
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal Penilaian & Gred Ketua Gelanggang */}
      {isGradingModalOpen && activeMember && (
        <Modal
          isOpen={isGradingModalOpen}
          onClose={() => setIsGradingModalOpen(false)}
          title={`Penilaian & Gred Silibus — ${formatPublicName(activeMember.name)}`}
        >
          <div className="space-y-4 text-xs">
            <div className="p-3 bg-slate-50 rounded-2xl border border-slate-200 space-y-1">
              <div className="flex justify-between">
                <span className="text-slate-500 font-bold">Nama Pesilat:</span>
                <strong className="text-slate-900">{activeMember.name}</strong>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 font-bold">Tahap Bengkung:</span>
                <strong className="text-slate-900">{activeMember.bengkung || 'Bengkung Putih'}</strong>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 font-bold">Kemajuan Silibus:</span>
                <strong className="text-asmm-red">{requiredCompletedCount}/{requiredTotalCount} Pelajaran Selesai ({progressPct}%)</strong>
              </div>
            </div>

            {/* Skala 1 hingga 5 Penguasaan Silibus */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="block text-xs font-extrabold text-slate-900">
                  Tahap Penguasaan Silibus (Skala 1 - 5):
                </label>
                <span className={`text-[10px] font-black px-2.5 py-0.5 rounded-full border ${RATING_LEVELS.find(r => r.value === gradingRating)?.badgeColor}`}>
                  {RATING_LEVELS.find(r => r.value === gradingRating)?.label}
                </span>
              </div>

              <div className="grid grid-cols-5 gap-1.5 sm:gap-2 pt-1 pb-1">
                {RATING_LEVELS.map((level) => {
                  const isSelected = gradingRating === level.value;
                  const isFilled = level.value <= gradingRating;
                  return (
                    <button
                      key={level.value}
                      type="button"
                      onClick={() => setGradingRating(level.value)}
                      className={`p-2 sm:p-2.5 rounded-xl border flex flex-col items-center justify-center gap-1 transition-all cursor-pointer ${isSelected
                        ? 'bg-amber-500 text-slate-950 border-amber-400 ring-2 ring-amber-400/30 shadow-xs font-black'
                        : isFilled
                          ? 'bg-amber-50/80 border-amber-200 text-amber-900 hover:bg-amber-100'
                          : 'bg-slate-50 border-slate-200 text-slate-400 hover:bg-slate-100'
                        }`}
                    >
                      <Star className={`w-4 h-4 sm:w-5 sm:h-5 ${isFilled ? 'fill-amber-400 text-amber-500' : 'text-slate-300'}`} />
                      <span className="text-[11px] font-black">Skala {level.value}</span>
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-600 bg-slate-50 p-2.5 rounded-xl border border-slate-200">
                {RATING_LEVELS.find(r => r.value === gradingRating)?.desc}
              </p>
            </div>

            {/* Remark / Ulasan Penilaian */}
            <div className="space-y-1">
              <label className="block text-xs font-extrabold text-slate-900">
                Remark / Ulasan Penilaian Ketua Gelanggang
              </label>
              <textarea
                rows={3}
                value={gradingNotes}
                onChange={(e) => setGradingNotes(e.target.value)}
                placeholder="Tulis ulasan/remark penilaian anda (cth: Penguasaan jurus mantap, pukulan 1 hingga 5 kemas dan menepati piawaian bengkung)..."
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white text-xs text-slate-900 outline-none leading-relaxed"
              />
            </div>

            {/* Quick suggested remarks */}
            <div className="space-y-1 pt-0.5">
              <span className="text-[10px] text-slate-500 font-bold block">Cadangan Remark Pantas:</span>
              <div className="flex items-center gap-1.5 flex-wrap">
                {[
                  'Penguasaan jurus kemas & menepati piawaian.',
                  'Kekuda kukuh & pukulan berpadu.',
                  'Perlu perkemaskan jurus 4 & 5 sebelum hari ujian.',
                  'Disiplin, adab silat & langkah sangat terpuji.'
                ].map((recText, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setGradingNotes(recText)}
                    className="text-[10px] px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg border border-slate-200 cursor-pointer transition-colors"
                  >
                    + {recText}
                  </button>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => handleSaveGrading('Lulus', gradingNotes, gradingRating)}
                disabled={isSavingGrading}
                className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs rounded-xl transition-all shadow-sm flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                <CheckCircle2 className="w-4 h-4" />
                <span>Sahkan Gred LULUS (Skala {gradingRating}/5)</span>
              </button>

              <button
                type="button"
                onClick={() => handleSaveGrading('Belum Dinilai', gradingNotes, gradingRating)}
                disabled={isSavingGrading}
                className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-all border border-slate-200 flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                <span>Simpan Ulasan (Belum Lulus)</span>
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
